import { rollTwoDice, rollDie } from '../rng/dice';
import { defFor } from '../cards/cardHelpers';
import { makeInstance } from '../state/createGameState';
import {
  isAbilityCharged, addCharge, spendCharges, findInstance, applyAtomicEffect, applyResourceToPlayer, chargesRequired,
} from './abilities';
import { shiftSector, stationRewardsForSector, deployedRewardsForSector } from './rewards';
import { WIN_VP_THRESHOLD, SECTORS } from '../constants';

function replacePlayer(state, playerId, updater) {
  const index = state.players.findIndex((p) => p.id === playerId);
  const players = state.players.slice();
  players[index] = updater(players[index]);
  return { ...state, players };
}

// Matt reported this directly: "I want to see a distribution of the
// dice rolls" (on the endgame stats screen), then, once that shipped:
// "they only have the rolls for the individual dice - that's not
// really what I want. I want to know the number of times that every
// number was rolled, so if you were to roll a six and a three I want
// us to add one to the six column, one to the three column, AND one
// to the nine column." One unified chart spanning every value that can
// actually come up this way - 1-6 for an individual die face, 2-12 for
// the combined total - not per-roll/per-player, since the dice are a
// shared resource everyone allocates from, not owned by whoever rolled
// them. valuesToTally is every value this call should credit (each
// caller below builds it), not just the individual die faces the name
// might suggest. Only ever called with values built from GENUINELY
// random rolls (rollDice/rerollDice's own rollDie/rollTwoDice output) -
// a setDice ability's preset value isn't a "roll" and shouldn't skew
// the distribution.
function recordDiceRolls(state, valuesToTally) {
  // A game saved before this stats field existed (a still-in-progress
  // local/remote game from before this shipped) won't have meta.stats
  // at all - default to empty rather than throwing, so it just starts
  // counting fresh from here instead of crashing on the next roll.
  const prevStats = state.meta.stats || { diceRollCounts: {}, totalCreditsEarned: {} };
  const diceRollCounts = { ...prevStats.diceRollCounts };
  valuesToTally.forEach((v) => { diceRollCounts[v] = (diceRollCounts[v] || 0) + 1; });
  return { ...prevStats, diceRollCounts };
}

export function rollDice(state) {
  if (state.meta.turnPhase !== 'pre-roll') return state;
  // Biodome's Cycolonic ("Before you roll on your turn, claim the top
  // card on the level 1 deck.") - mandatory and automatic, every one of
  // the owner's own turns, resolved right here so it genuinely precedes
  // the roll. Straight to deployed if a colony holds the card's sector
  // (the standard Genesis claim rule); silently skipped only when the
  // level 1 draw pile has run dry.
  {
    const active = state.players[state.meta.activePlayerIndex];
    if (ownsOngoingModifier(active, 'claimBeforeRoll') && state.shipyard.draw[1].length) {
      const [topCardId, ...rest] = state.shipyard.draw[1];
      state = {
        ...state,
        shipyard: { ...state.shipyard, draw: { ...state.shipyard.draw, 1: rest } },
      };
      const claimedDef = defFor(topCardId);
      state = sectorHasColony(state.players.find((p) => p.id === active.id), claimedDef.sector)
        ? deployCardIntoColonizedSector(state, active.id, claimedDef, claimedDef.sector)
        : deployCard(state, active.id, claimedDef, claimedDef.sector);
    }
  }
  const preset = state.meta.dice?.preset ? state.meta.dice.values : null;
  let values = preset || [];
  let { rngState } = state.meta;
  const rolledFaces = [];
  if (!preset || preset[0] == null || preset[1] == null) {
    const [d1d2, nextRng] = rollTwoDice(rngState);
    rngState = nextRng;
    values = [preset?.[0] ?? d1d2[0], preset?.[1] ?? d1d2[1]];
    if (preset?.[0] == null) rolledFaces.push(d1d2[0]);
    if (preset?.[1] == null) rolledFaces.push(d1d2[1]);
  }
  // The resulting TOTAL only counts as a real "roll" alongside the
  // individual faces above if at least one of the two dice was actually
  // random this time - a fully preset roll (both dice preset) has
  // nothing genuinely random to credit at all, same reasoning as the
  // individual-face case.
  const valuesToTally = rolledFaces.length ? [...rolledFaces, values[0] + values[1]] : rolledFaces;
  // Genesis's Vlad ("Choose a number. Until the end of your next turn,
  // EACH PLAYER, before their normal roll, sets one die to that
  // number.") - overrides die 0 AFTER the normal roll/preset logic
  // above, so the RNG consumption sequence stays byte-for-byte identical
  // whether or not Vlad is active (every other card's own seeded
  // behavior is unaffected) - only the die 0 VALUE the rest of the turn
  // actually allocates against changes. Stats still tally the genuinely
  // random roll underneath, not the forced override - a defensible
  // simplification (recordDiceRolls' own comment already treats a
  // preset value as "not a roll" for the exact same reason). Doesn't
  // apply to rerollDice below - the card's own text says "before their
  // NORMAL roll", and a reroll is a distinct action.
  // Biodome's Hu-Xiu ("Whenever you roll on your turn, you only roll 1
  // die. Then set the other die to the same number.") - applied the same
  // way as Vlad below: AFTER the normal roll logic, so RNG consumption
  // stays byte-for-byte identical whether or not the colony is in play.
  // When both are somehow active, Vlad's force lands last on die 0 -
  // "sets one die to that number" reads as overriding whatever that die
  // showed, mirrored or not.
  const activeOwnsMirror = ownsOngoingModifier(state.players[state.meta.activePlayerIndex], 'mirrorDice');
  if (activeOwnsMirror) values = [values[0], values[0]];
  const forced = state.meta.forcedDieValue;
  const finalValues = (forced && forced.turnsRemaining > 0) ? [forced.value, values[1]] : values;
  return {
    ...state,
    meta: {
      ...state.meta,
      rngState,
      dice: {
        values: finalValues, allocations: {}, sectorShift: {}, totalModifier: {}, dieAdjustments: {},
      },
      turnPhase: 'awaiting-allocation',
      stats: recordDiceRolls(state, valuesToTally),
    },
  };
}

export function rerollDice(state, dieIndex) {
  // dieIndex: 0, 1, or null (both).
  if (!state.meta.dice) return state;
  const [d1, r1] = rollDie(state.meta.rngState);
  let values = state.meta.dice.values.slice();
  let rngState = r1;
  let rolledFaces = [d1];
  if (dieIndex === null) {
    const [d2, r2] = rollDie(r1);
    values = [d1, d2];
    rngState = r2;
    rolledFaces = [d1, d2];
  } else {
    values[dieIndex] = d1;
  }
  // Unlike rollDice above, a reroll is never fully preset - rolledFaces
  // always has at least one genuinely random value, so the resulting
  // total always counts too.
  const valuesToTally = [...rolledFaces, values[0] + values[1]];
  return {
    ...state,
    meta: {
      ...state.meta, rngState, dice: { ...state.meta.dice, values }, stats: recordDiceRolls(state, valuesToTally),
    },
  };
}

// Resolves an arrow reward, following the chain through as many hops as
// the arrows actually dictate (rulebook p19/p20, and the official May 2018
// FAQ's "How many rewards may I claim with the use of an arrow pointing at
// a sector?" - "you claim all of the rewards in the neighboring sector
// where the arrow was pointed", including that sector's OWN arrow if it
// has one).
//
// Matt reported this directly, most recently: "We need [arrows] to work
// properly when they are blue (on my board) or red (deployed). An arrow
// should grant all of the bonuses it points at including any combined
// values in our deployed UI. It should also grant any arrows that point
// further on... I've seen it not giving me things I was owed." Two real
// bugs, both in how a chain hop looked up its target (lookupSector,
// applyHit's own closure below): it always read the target's STATION
// side regardless of which zone (station/deployed - "blue"/"red")
// actually originated the chain, and it only ever read a single card's
// `reward`, silently dropping any `secondaryReward` ("combined values")
// on every hop past the very first. A blue arrow always chains into
// exactly one card (a sector's station never stacks) but a red arrow
// chains into the neighbor's DEPLOYED side instead - which can hold
// SEVERAL cards at once (every card ever placed in a sector keeps
// contributing there once bumped off station - see applyHit's own
// deployedStack.forEach for the direct-hit equivalent this mirrors).
// `lookupSector` now returns that whole list (0, 1, or many slotDefs)
// rather than a single reward, and every slotDef in it is resolved fully
// and independently right here - own base reward, own secondaryReward,
// and (if it has one) its own further chain, all the way down - a target
// sector with several stacked deployed cards behaves exactly as if all of
// them had been hit directly, whether reached by a direct hit or an
// arrow.
//
// `visited` is the loop guard the FAQ's own "Can I use two adjacent
// arrows to bounce... and claim infinite rewards?" answer requires (an
// explicit no, rulebook p17's note) - a sector already in `visited` is
// treated the same as an edge with nothing beyond it.
//
// Matt reported this directly, twice, earlier: "The card in my seven
// slot... should've given me the money printed on it plus an arrow to
// the right, and it didn't give me the arrow money." l1-merbold prints
// "1 credit + an arrow pointing right" - a real base reward of its own,
// not just a bare arrow - so both the base reward AND whatever the arrow
// resolves to get granted, never one replacing the other, per hop.
//
// A direction:'both' arrow (l1-parker/shaw/lichtenberg/thornton, all
// "choose the sector to your left or right") produces a `choiceGroups`
// entry instead of resolving immediately, once both neighbors are in
// range - each candidate option is itself a fully resolved sub-chain (see
// resolveArrowChoice's own comment for how a second choice nested inside
// one branch gets handled). More than one entry in `choiceGroups` means
// more than one card at THIS hop independently hit its own both-direction
// arrow - rare (needs the same sector to have accumulated multiple
// distinct arrow-printed cards over its deployed history), but every
// entry still gets its own independent pending choice rather than only
// the first being honored.
//
// Genesis/Shy Pluto's "Diagonal Arrows" and Terra Proxima's "Directional
// Arrows" (verified word-for-word against both rulebooks - genuinely
// identical mechanic, just two different flavor-icon shapes: diagonal is
// shift 1 left/right, directional's straight variant is shift 0/"same
// sector" - see shiftSector's own comment): "When this card is deployed,
// they can trigger station rewards, and while in station they can
// trigger deployed rewards" - i.e. the target is looked up in the
// OPPOSITE zone from wherever this arrow itself lives, not the same one
// every other arrow in the game uses. `zone` is threaded through the
// recursion explicitly now (no longer fixed for the whole walk) so it
// can flip on exactly the hop that has `crossZone: true` and nowhere
// else - each arrow decides its OWN target zone independently, so a
// normal same-zone arrow chaining INTO a cross-zone one (or vice versa)
// still flips (or doesn't) at exactly the right link.
//
// `visited` entries are now sector+zone pairs (see visitKey below), not
// just sector numbers - a same-sector (shift 0) cross-zone arrow
// deliberately targets its OWN sector, just the other zone, which a
// plain sector-only visited set would have wrongly blocked as "already
// claimed this chain" (it's the ORIGIN'S OWN zone that's actually
// visited, not the sector as a whole).
function oppositeZone(zone) {
  return zone === 'station' ? 'deployed' : 'station';
}

// A player's own sector's reward slotDef(s) for a given zone - shared by
// applyHit's own lookupSectorInZone closure and resolveSectorPick below
// (Shy Pluto's "gain rewards for any N sectors 1-6" - a picked sector's
// reward is resolved through this exact same lookup, not a special case).
// Arrows (and this pick) never reach onto another PLAYER's board, only
// the acting player's own - so this always resolves against `playerId`'s
// own sectors regardless of who originally triggered the grant.
function ownSectorSlotDefs(state, playerId, targetSector, zone) {
  const player = state.players.find((p) => p.id === playerId);
  if (zone === 'station') {
    const { slot } = stationRewardsForSector(player, targetSector);
    return slot ? [slot] : [];
  }
  return deployedRewardsForSector(player, targetSector).map(({ slot }) => slot).filter(Boolean);
}

function visitKey(sector, zone) {
  return `${sector}:${zone}`;
}

// Shy Pluto's "Hard Way" rewards (rulebook p8, verified word for word):
// "When doubles are rolled, you either gain a reward in place of the
// standard reward on the card or you gain a reward conditionally based
// on rolling doubles" - tied to ONE SPECIFIC printed face value (a card's
// own Hard Way triggers on double-4s specifically, for example, not "any
// double"). Modeled as `reward.hardWay = {value: N, reward: {...}}` -
// when the CURRENT roll was doubles of exactly N, the entire reward is
// swapped for `hardWay.reward`, unconditionally. This single
// substitution covers BOTH printed variants: the "replace" example
// (normally 2, but 8 on double-4) and the "conditional-only" example
// (normally nothing, but 7 on double-4) are the same mechanic - the
// second is just a card whose own non-hardWay `reward` happens to grant
// nothing (kind: 'text', no effect) rather than a real base amount.
function resolveHardWay(reward, hardWayFace) {
  if (!reward.hardWay || hardWayFace == null || reward.hardWay.value !== hardWayFace) return reward;
  return reward.hardWay.reward;
}

function resolveArrowChain(slotDefs, ownSector, zone, lookupSector, visited) {
  const ownBaseRewards = [];
  const choiceGroups = [];
  let leafReward = null;

  // Recurses into one already-resolved-and-visited-checked target sector,
  // returning its own {ownBaseRewards, leafReward, choiceGroups} - shared
  // by the plain single-target hop, each option of a `direction: 'both'`
  // choice, and Arrow+Arrows' own always-both-granted extra hop below, so
  // all three stay identical about how a target sector's own rewards
  // (including further arrows) get resolved.
  const resolveTarget = (target, targetZone) => resolveArrowChain(
    lookupSector(target, targetZone),
    target,
    targetZone,
    lookupSector,
    new Set([...visited, visitKey(target, targetZone)]),
  );

  slotDefs.forEach((slotDef) => {
    if (!slotDef?.reward) return;
    if (slotDef.secondaryReward) ownBaseRewards.push(slotDef.secondaryReward);
    const { reward } = slotDef;
    const ownBaseReward = reward.kind !== 'text' && reward.amount ? reward : null;
    if (ownBaseReward) ownBaseRewards.push(ownBaseReward);
    if (!reward.arrow) {
      if (reward.kind === 'text') leafReward = reward;
      return;
    }
    const targetZone = reward.arrow.crossZone ? oppositeZone(zone) : zone;

    // Shy Pluto's "Arrow+Arrows" (rulebook p9, verified word for word):
    // "you then also gain the rewards for both the sector one space to
    // the left and 2 spaces to the left" - an UNCONDITIONAL second hop,
    // one shift further than the arrow's own printed shift, same
    // direction/zone as the primary arrow. Always BOTH granted - unlike
    // `direction: 'both'` below, this is never a player choice between
    // alternatives, so it's resolved as its own independent hop entirely
    // outside the targets.length branching below (whatever the primary
    // shift resolves to - one target, several, or none - doesn't affect
    // whether this extra one fires).
    if (reward.arrow.chainExtra) {
      const extraTargets = shiftSector(ownSector, { ...reward.arrow, shift: reward.arrow.shift + 1 })
        .filter((s) => !visited.has(visitKey(s, targetZone)));
      extraTargets.forEach((target) => {
        const deeper = resolveTarget(target, targetZone);
        ownBaseRewards.push(...deeper.ownBaseRewards);
        if (deeper.leafReward) leafReward = deeper.leafReward;
        choiceGroups.push(...deeper.choiceGroups);
      });
    }

    const targets = shiftSector(ownSector, reward.arrow).filter((s) => !visited.has(visitKey(s, targetZone)));
    if (!targets.length) return;
    if (targets.length === 1) {
      const deeper = resolveTarget(targets[0], targetZone);
      ownBaseRewards.push(...deeper.ownBaseRewards);
      if (deeper.leafReward) leafReward = deeper.leafReward;
      choiceGroups.push(...deeper.choiceGroups);
    } else {
      const options = targets.map((target) => {
        const deeper = resolveTarget(target, targetZone);
        return {
          sector: target, leafReward: deeper.leafReward, ownBaseRewards: deeper.ownBaseRewards, choiceGroups: deeper.choiceGroups,
        };
      });
      choiceGroups.push({ options });
    }
  });

  return { ownBaseRewards, leafReward, choiceGroups };
}

// Shared by applyHit's normal path and resolveArrowChoice's after-a-pick
// path below - given a fully-resolved chain result (see resolveArrowChain
// above), grants every accumulated ownBaseReward (every hop's own base
// reward AND secondaryReward, station or deployed, direct hit or
// chained-through - see resolveArrowChain's own comment) plus the chain's
// own leaf text-effect reward (claim/buy) if any, and stashes one pending
// arrow choice per entry in choiceGroups (almost always 0 or 1 - see
// resolveArrowChain's own comment on why more than one is possible but
// rare). One place so every caller shares the exact same bookkeeping
// (pendingClaims/pendingExtraBuys plumbing included).
//
// `scoped` (Genesis/Shy Pluto Bonus Rolls - enqueueBonusRoll below) just
// passes straight through to grantReward - see its own comment.
function settleArrowChain(state, playerId, chain, sourceSector, instanceId, scoped = false) {
  let nextState = state;
  const pendingClaims = [];
  const pendingExtraBuys = [];
  const pendingSectorPicks = [];
  const pendingChargeChoices = [];
  const pendingMoveChoices = [];
  const pendingCardSwapChoices = [];
  const pendingEmptySectorChoices = [];
  chain.ownBaseRewards.forEach((baseReward) => {
    const granted = grantReward(nextState, playerId, baseReward, scoped, sourceSector);
    nextState = granted.state;
    pendingClaims.push(...granted.pendingClaims);
    pendingExtraBuys.push(...granted.pendingExtraBuys);
    pendingSectorPicks.push(...granted.pendingSectorPicks);
    pendingChargeChoices.push(...granted.pendingChargeChoices);
    pendingMoveChoices.push(...granted.pendingMoveChoices);
    pendingCardSwapChoices.push(...granted.pendingCardSwapChoices);
    pendingEmptySectorChoices.push(...granted.pendingEmptySectorChoices);
  });
  if (chain.leafReward) {
    const granted = grantReward(nextState, playerId, chain.leafReward, scoped, sourceSector);
    nextState = granted.state;
    pendingClaims.push(...granted.pendingClaims);
    pendingExtraBuys.push(...granted.pendingExtraBuys);
    pendingSectorPicks.push(...granted.pendingSectorPicks);
    pendingChargeChoices.push(...granted.pendingChargeChoices);
    pendingMoveChoices.push(...granted.pendingMoveChoices);
    pendingCardSwapChoices.push(...granted.pendingCardSwapChoices);
    pendingEmptySectorChoices.push(...granted.pendingEmptySectorChoices);
  }
  const pendingArrowChoices = chain.choiceGroups.map((group) => ({
    playerId, sourceSector, instanceId, options: group.options,
  }));
  // pendingMoveChoices/pendingCardSwapChoices (Triumph/Ulric/Maddock)
  // used to only ever be granted via buyCard's own immediateReward call
  // site (a one-time deploy trigger), which collects them directly from
  // grantReward itself - this function never needed to forward them.
  // Genesis's Valiant/Shy Pluto's Leslie (exchangeWithAdjacentSector) are
  // the first cards to grant one of these as a normal per-hit BLUE/RED
  // reward, reached through THIS function instead - without forwarding
  // them here, a real dice hit would silently drop the resulting pending
  // choice on the floor (found writing exchangeWithAdjacentSector's own
  // test: the choice just never appeared after allocating dice).
  return {
    state: nextState, pendingClaims, pendingExtraBuys, pendingSectorPicks, pendingChargeChoices, pendingMoveChoices, pendingCardSwapChoices, pendingArrowChoices, pendingEmptySectorChoices,
  };
}

// Grants a single already-resolved (non-arrow) reward to a player -
// shared by applyHit's normal per-sector-hit path below and
// resolveArrowChoice's after-the-fact path (once a "choose left or
// right" pick is made, the reward at the CHOSEN sector still needs
// identical handling: a plain resource, or a text effect like
// claimCard/buyCard).
//
// `scoped` (Genesis/Shy Pluto Bonus Rolls - enqueueBonusRoll below):
// both rulebooks, word for word, "any effect that would cause you to
// affect other players, the shipyard, or the unbought colony cards is
// ignored" during a bonus roll. claimCard/buyCard both reach into the
// shared shipyard, so both are suppressed entirely when scoped, rather
// than queuing a pending claim/extra-buy a bonus roll was never supposed
// to grant. bonusRoll and pickSectors are both checked BEFORE the scoped
// gate on purpose - neither one "affects the shipyard/other players":
// bonusRoll is a further PRIVATE roll for the same player (so nested
// bonus rolls still chain normally even while already scoped), and
// pickSectors (Shy Pluto's "gain rewards for any N sectors 1-6" -
// resolveSectorPick below) only ever grants the SAME player's own board
// rewards, same as a plain resource reward would. `sourceSector`
// (several Genesis cards' own "gain X for each charge in this and
// adjacent sectors" - see resolveScaledReward's own comment) is the
// sector the granting card itself lives in - null for callers with no
// such context (a colony/ship's own buy-time immediateReward/
// sharedReward, or an endOfTurnReward), which is fine since none of
// those currently carry a scaledReward effect that would need it.
function grantReward(state, playerId, reward, scoped = false, sourceSector = null) {
  const pendingClaims = [];
  const pendingExtraBuys = [];
  const pendingSectorPicks = [];
  const pendingChargeChoices = [];
  const pendingMoveChoices = [];
  const pendingCardSwapChoices = [];
  const pendingEmptySectorChoices = [];
  if (!reward) return {
    state, pendingClaims, pendingExtraBuys, pendingSectorPicks, pendingChargeChoices, pendingMoveChoices, pendingCardSwapChoices, pendingEmptySectorChoices,
  };
  if (reward.kind === 'text') {
    const effect = reward.effect;
    if (effect?.kind === 'bonusRoll') {
      // Genesis's Mary ("gain 4 bonus rolls" on buy, "gain 2 bonus rolls"
      // on deployed, per Matt's own direct confirmation - the original
      // transcription's "reroll x4/x2" icon-count guess was wrong, these
      // are bonusRoll's own printed count, not rerollDice) - `count`
      // enqueues that many independent private rolls (enqueueBonusRoll's
      // own comment), each resolved one at a time via a real player
      // choice, not synchronously here. Every other bonusRoll card omits
      // `count` and gets exactly 1, unchanged from before this existed.
      let nextState = state;
      for (let i = 0; i < (effect.count || 1); i += 1) nextState = enqueueBonusRoll(nextState, playerId);
      return {
        state: nextState, pendingClaims, pendingExtraBuys, pendingSectorPicks, pendingChargeChoices, pendingMoveChoices, pendingCardSwapChoices, pendingEmptySectorChoices,
      };
    }
    if (effect?.kind === 'pickSectors') {
      // Self/cross-reference guard: every currently-wired pickSectors
      // card (Jammer/Asclepius/Parise/Ochoa/Ivins/Vetrov/Mandela/Auel)
      // prints its OWN sector outside the 1-6 range it picks from, so
      // this can't happen through normal placement - but `exchangeCard`
      // (abilities.js) freely swaps a player's own cards between sectors
      // with no restriction, so a pickSectors card CAN end up sitting
      // inside its own pick range. Without this guard, choosing that
      // sector grants its own reward again - another identical pending
      // pick, re-offering the exact same sector forever (found via
      // selfPlaySmoke.test.js hanging once Flying Dutchman's addition
      // shifted a seed's shipyard draws enough to reach this combo). A
      // single `sourceSector` exclusion only stops a card looping into
      // ITSELF though - with more than one such card now wired in, TWO
      // different relocated pickSectors cards can ping-pong off each
      // other forever instead (A's pick includes B's sector, grants B's
      // reward, whose own pick includes A's sector again, since A was
      // never excluded from B's own pick) - found the same way, a
      // different seed once Vetrov/Mandela/Auel joined the pool.
      // `excludeSectors` accumulates every sector already "spent"
      // anywhere in this causal chain (threaded forward by
      // resolveSectorPick's own per-chosen-sector grant below, which
      // merges its OWN pick's accumulated excludes plus every sector it
      // just chose into whatever new pick this call produces) rather
      // than resetting to just the immediate parent each time - closes
      // both the 1-cycle and any longer cycle, without banning the
      // legitimate combo of chaining into a genuinely fresh sector's own
      // pickSectors reward.
      pendingSectorPicks.push({
        playerId,
        count: effect.count,
        zone: effect.zone || 'station',
        min: effect.min ?? 1,
        max: effect.max ?? 6,
        excludeSectors: sourceSector != null ? [sourceSector] : [],
      });
      return {
        state, pendingClaims, pendingExtraBuys, pendingSectorPicks, pendingChargeChoices, pendingMoveChoices, pendingCardSwapChoices, pendingEmptySectorChoices,
      };
    }
    if (effect?.kind === 'sectorRangeReward') {
      return {
        state: resolveSectorRangeReward(state, playerId, effect), pendingClaims, pendingExtraBuys, pendingSectorPicks, pendingChargeChoices, pendingMoveChoices, pendingCardSwapChoices, pendingEmptySectorChoices,
      };
    }
    if (effect?.kind === 'scaledReward') {
      return {
        state: resolveScaledReward(state, playerId, effect, sourceSector), pendingClaims, pendingExtraBuys, pendingSectorPicks, pendingChargeChoices, pendingMoveChoices, pendingCardSwapChoices, pendingEmptySectorChoices,
      };
    }
    if (effect?.kind === 'neighborStationReward') {
      // The Flying Dutchman (see resolveNeighborStationReward's own
      // comment) - reads, never modifies, another player's own board, so
      // this resolves before the scoped gate same as scaledReward below.
      return {
        state: resolveNeighborStationReward(state, playerId, sourceSector), pendingClaims, pendingExtraBuys, pendingSectorPicks, pendingChargeChoices, pendingMoveChoices, pendingCardSwapChoices, pendingEmptySectorChoices,
      };
    }
    if (effect?.kind === 'chargeAllReward') {
      // Watts's own one-time reward version of Nikola's ability (see
      // chargeAllEligibleCards's own comment) - only ever touches the
      // SAME player's own board, same reasoning as scaledReward/
      // sectorRangeReward above for resolving before the scoped gate.
      return {
        state: chargeAllEligibleCards(state, playerId), pendingClaims, pendingExtraBuys, pendingSectorPicks, pendingChargeChoices, pendingMoveChoices, pendingCardSwapChoices, pendingEmptySectorChoices,
      };
    }
    if (effect?.kind === 'placeChargeUpToN') {
      // Genesis's Helios/Radiant/Watts (deployed) - "Place 1 charge on up
      // to 3 different cards in this sector or lower." Re-verified
      // directly against the card art as plain (no charge-square
      // graphic) one-time rewards, not chargeable abilities. "Up to N" is
      // a genuine player choice, including choosing FEWER than N or none
      // at all (unlike pickSectors' own EXACT count) - queued the same
      // way as every other delayed choice, resolved via
      // RESOLVE_CHARGE_CHOICE. Only ever touches the same player's own
      // board (which cards on THEIR OWN board to charge), same reasoning
      // as scaledReward/chargeAllReward above for resolving before the
      // scoped gate. `maxSector` null (no sourceSector context, e.g. a
      // colony's own buy-time reward) means "no sector limit" rather than
      // "no sectors qualify" - matches no card confirmed to need this
      // without a sourceSector context, but fails open, not closed.
      pendingChargeChoices.push({ playerId, max: effect.max, maxSector: sourceSector });
      return {
        state, pendingClaims, pendingExtraBuys, pendingSectorPicks, pendingChargeChoices, pendingMoveChoices, pendingCardSwapChoices, pendingEmptySectorChoices,
      };
    }
    if (effect?.kind === 'moveDeployedCard') {
      // Genesis's Triumph/Ulric (Cards 92/70) - re-verified directly
      // against the card art as plain Immediate Rewards (rulebook p3),
      // not chargeable abilities as originally transcribed. "Move 1
      // deployed card into sector X" (a fixed list of destinations,
      // printed per-card). Immediate Rewards can never be gained via
      // arrows/dice-arrow abilities (rulebook p3's own explicit rule) -
      // unlike every other delayed-choice effect in this switch, this is
      // ONLY ever reached from buyCard's own immediateReward call site,
      // never chained through settleArrowChain/applyHit/resolveSectorPick/
      // etc. Deliberately NOT threaded through that whole plumbing as a
      // result (no card confirmed to need it anywhere else) -
      // pendingMoveChoices only needs to survive the one direct
      // grantReward call buyCard's own immediateReward handling makes.
      // No pending entry at all if there's nothing to move - matches
      // "you MAY move the card you deployed" (Card 92's own
      // clarification) reading as "if a legal move exists," not a
      // forced action with no legal targets. In practice this guard is
      // effectively always true by the time it runs: buyCard's own
      // deployCard call (which places THIS card and bumps whatever was
      // previously at its own sector) always runs BEFORE immediateReward
      // resolves, so the freshly-bumped former occupant already counts
      // as "something deployed" - kept anyway as a real, correct
      // condition rather than an unconditional push, in case a future
      // card ever reaches this effect through a path that doesn't bump
      // anything first.
      // Ulric (Card 70) is the generalization this comment already
      // anticipated: "Move 1 deployed card ONE SECTOR TO THE LEFT OR
      // RIGHT" - the legal destinations depend on whichever card the
      // player ends up choosing to move (that card's OWN current sector
      // +-1), not a fixed printed list like Triumph's - `adjacentOnly`
      // signals resolveMoveChoice to compute the pair live instead of
      // checking against `destinations`.
      if (Object.values(state.players.find((p) => p.id === playerId).sectors).some((s) => s.deployedStack.length > 0)) {
        pendingMoveChoices.push({ playerId, destinations: effect.destinations, adjacentOnly: effect.adjacentOnly });
      }
      return {
        state, pendingClaims, pendingExtraBuys, pendingSectorPicks, pendingChargeChoices, pendingMoveChoices, pendingCardSwapChoices, pendingEmptySectorChoices,
      };
    }
    if (effect?.kind === 'exchangeAdjacentCard') {
      // Genesis's Maddock (Card 69) - "Exchange any 2 ship cards in
      // adjacent sectors with each other." Matt's own clarification: pick
      // ONE card from each of two adjacent sectors - both stations, or
      // both a specific deployed instance (never one of each) - and swap
      // just those two, leaving everything else in place. A plain
      // one-time Immediate Reward, same "never via arrows, only reached
      // from buyCard's own immediateReward call site" shape as
      // moveDeployedCard just above - always legal to queue (every sector
      // always has a station, so a station<->station swap is always
      // available even with nothing deployed anywhere).
      pendingCardSwapChoices.push({ playerId });
      return {
        state, pendingClaims, pendingExtraBuys, pendingSectorPicks, pendingChargeChoices, pendingMoveChoices, pendingCardSwapChoices, pendingEmptySectorChoices,
      };
    }
    // Genesis's Valiant / Shy Pluto's Leslie (rulebook Card 28's own
    // clarification for Valiant, verified word for word: "This ship
    // allows you to exchange it with any card on your command console
    // that is in the sector to the left or right.") - a real per-hit
    // BLUE/RED reward (fires every time this sector is hit, unlike
    // Maddock's own one-time orange Immediate Reward above), anchored to
    // THIS card's own sector rather than any adjacent pair on the board.
    // Reuses the exact same pendingCardSwapChoices/resolveCardSwapChoice
    // machinery, just with a new optional `anchorSector` field requiring
    // one side of the swap to be this card's own sector - Maddock's own
    // entries simply omit it and are unaffected.
    //
    // Simplification, documented rather than guessed: the rulebook
    // distinguishes station-activation ("may exchange with a colony")
    // from deployed-activation ("may only exchange with a ship"), which
    // would require a genuine cross-zone (station<->deployed) swap of
    // THIS specific instance when deployed - this engine's own
    // resolveCardSwapChoice only ever swaps same-zone pairs. Modeled
    // uniformly as a station<->station swap between this card's own
    // sector and the adjacent one, regardless of which zone actually
    // triggered it - correct for the common (station-activation) case,
    // an approximation for the rarer deployed-activation one.
    if (effect?.kind === 'exchangeWithAdjacentSector') {
      pendingCardSwapChoices.push({ playerId, anchorSector: sourceSector });
      return {
        state, pendingClaims, pendingExtraBuys, pendingSectorPicks, pendingChargeChoices, pendingMoveChoices, pendingCardSwapChoices, pendingEmptySectorChoices,
      };
    }
    if (effect?.kind === 'emptySectorRange') {
      // Genesis's Pharos (station) - "Deploy all your ship cards in
      // sectors 7-12." A plain one-time Immediate Reward (rulebook p3),
      // same "never via arrows, only reached from buyCard's own
      // immediateReward call site" shape as moveDeployedCard/
      // exchangeAdjacentCard above - unconditional, no pending choice
      // needed (every qualifying sector just empties outright).
      let nextState = state;
      for (let s = effect.min; s <= effect.max; s += 1) nextState = emptySector(nextState, playerId, s);
      return {
        state: nextState, pendingClaims, pendingExtraBuys, pendingSectorPicks, pendingChargeChoices, pendingMoveChoices, pendingCardSwapChoices, pendingEmptySectorChoices,
      };
    }
    if (effect?.kind === 'emptySectorChoice') {
      // Genesis's Pharos (deployed) - "Deploy any 1 ship card." Unlike
      // the station side, this IS reachable through the normal dice-hit/
      // arrow-chain system (a plain deployed reward, not an
      // immediateReward), so it needs a real pending-choice queue
      // threaded through the same plumbing pickSectors/placeChargeUpToN
      // already use - only queued if the player actually has at least
      // one occupied sector to empty (same "don't offer a choice with no
      // legal targets" reasoning as moveDeployedCard's own guard).
      const player = state.players.find((p) => p.id === playerId);
      const hasOccupiedSector = Object.values(player.sectors).some((s) => s.station);
      if (hasOccupiedSector) pendingEmptySectorChoices.push({ playerId });
      return {
        state, pendingClaims, pendingExtraBuys, pendingSectorPicks, pendingChargeChoices, pendingMoveChoices, pendingCardSwapChoices, pendingEmptySectorChoices,
      };
    }
    if (scoped) {
      return {
        state, pendingClaims, pendingExtraBuys, pendingSectorPicks, pendingChargeChoices, pendingMoveChoices, pendingCardSwapChoices, pendingEmptySectorChoices,
      };
    }
    // Several Genesis ships (Greenhaven/Lugger/De León/The Flying
    // Dutchman) show a plain reroll icon as a REWARD, not a chargeable
    // ability - granted directly, reusing the existing rerollDice()
    // (already what a base-game reroll ABILITY calls under the hood -
    // see useAbility's own rerollDice case). Checked AFTER the scoped
    // gate, unlike bonusRoll/pickSectors/scaledReward above - rerolling
    // the SHARED turn dice changes every other player's own allocation
    // too, exactly the "affect other players" a Bonus Roll's own rules
    // text says to ignore, so this stays suppressed while scoped. Only
    // ever 'both' confirmed on any existing card (Volynov, the only real
    // base-game user of this ability) - no card is confirmed to need a
    // single-die reroll, so that's not modeled until one actually does.
    if (effect?.kind === 'rerollReward') {
      return {
        state: rerollDice(state, null), pendingClaims, pendingExtraBuys, pendingSectorPicks, pendingChargeChoices, pendingMoveChoices, pendingCardSwapChoices, pendingEmptySectorChoices,
      };
    }
    if (effect?.kind === 'claimCard') pendingClaims.push(...effect.claims.map((c) => ({ ...c, playerId })));
    if (effect?.kind === 'buyCard') pendingExtraBuys.push({ playerId, free: true, sectorRange: null });
    if (effect?.kind === 'peekAndClaim') {
      // Genesis's Dalton - re-verified directly against the card art as
      // a plain (orange, no charge squares) one-time reward, not a
      // chargeable ability (useAbility's own peekAndClaim case still
      // exists too, for a hypothetical future card that DOES gate this
      // behind charges). Suppressed while scoped, same reasoning as
      // claimCard/buyCard just above - drawing from the shared shipyard
      // draw pile reaches into shared state a Bonus Roll's own rules
      // text says to leave alone.
      return {
        state: queueDeckPeek(state, playerId, effect.level, effect.count), pendingClaims, pendingExtraBuys, pendingSectorPicks, pendingChargeChoices, pendingMoveChoices, pendingCardSwapChoices, pendingEmptySectorChoices,
      };
    }
    return {
      state, pendingClaims, pendingExtraBuys, pendingSectorPicks, pendingChargeChoices, pendingMoveChoices, pendingCardSwapChoices, pendingEmptySectorChoices,
    };
  }
  return {
    state: applyResourceToPlayer(state, playerId, reward), pendingClaims, pendingExtraBuys, pendingSectorPicks, pendingChargeChoices, pendingMoveChoices, pendingCardSwapChoices, pendingEmptySectorChoices,
  };
}

// Applies whatever a single player gains for one "hit" (one sector number
// their dice allocation resolved to) this roll. `isActive` picks station
// vs deployed. `scoped` (Genesis/Shy Pluto Bonus Rolls - enqueueBonusRoll
// below) passes straight through to settleArrowChain - see grantReward's
// own comment for what it actually suppresses. `hardWayFace` (Shy Pluto's
// own Hard Way rewards - see resolveHardWay's own comment) is the exact
// doubled face value if the roll THIS HIT came from was doubles, else
// null - independent of sum/separate allocation choice, since "when
// doubles are rolled" describes the physical roll, not how it's
// allocated. Returns { state, pendingClaims: [...], pendingExtraBuys:
// [...], pendingSectorPicks: [...], pendingArrowChoices: [...] }.
function applyHit(state, playerId, sectorNum, isActive, scoped = false, hardWayFace = null) {
  let nextState = state;
  const pendingClaims = [];
  const pendingExtraBuys = [];
  const pendingArrowChoices = [];
  const pendingSectorPicks = [];
  const pendingChargeChoices = [];
  const pendingMoveChoices = [];
  const pendingCardSwapChoices = [];
  const pendingEmptySectorChoices = [];
  const player = nextState.players.find((p) => p.id === playerId);

  // Matt reported this directly: "We need [arrows] to work properly when
  // they are blue (on my board) or red (deployed)." `zone` starts as
  // whichever side (station/deployed) originated this particular hit's
  // arrow, and stays that way hop to hop UNLESS a crossZone arrow
  // (Diagonal/Directional Arrows - see resolveArrowChain's own comment)
  // flips it for that one link - resolveArrowChain itself decides that
  // per hop now, this is just the plain zone -> slotDefs lookup it calls
  // with whichever zone it's currently asking about. See
  // resolveArrowChain's own comment for why deployed can return several
  // slotDefs at once where station always returns at most one. Delegates
  // to ownSectorSlotDefs (shared with resolveSectorPick below) - reads
  // `nextState` fresh on each call, not a stale snapshot, since
  // applySlotEntry's own ability-charging branch can mutate it mid-walk.
  const lookupSectorInZone = (targetSector, zone) => ownSectorSlotDefs(nextState, playerId, targetSector, zone);

  const applySlotEntry = (slotDef, instance, slotKey) => {
    if (!slotDef) return;
    if (slotDef.ability) {
      if (!isAbilityCharged(instance, slotKey, slotDef.ability, nextState.players.length)) {
        nextState = replacePlayer(nextState, playerId, (p) => ({
          ...p,
          sectors: {
            ...p.sectors,
            [sectorNum]: {
              ...p.sectors[sectorNum],
              [slotKey === 'station' ? 'station' : 'deployedStack']: slotKey === 'station'
                ? addCharge(instance, slotKey, slotDef.ability.squares)
                : p.sectors[sectorNum].deployedStack.map((d) => (
                  d.instanceId === instance.instanceId ? addCharge(d, slotKey, slotDef.ability.squares) : d
                )),
            },
          },
        }));
      }
      if (slotDef.ability.bonusReward) {
        nextState = applyResourceToPlayer(nextState, playerId, slotDef.ability.bonusReward);
      }
      // Deliberately no early return here - Genesis's Pegasus (see
      // docs/expansion-decisions-needed.md's own "dual reward+ability"
      // mystery) prints a real plain reward (Bonus Roll) ALONGSIDE its own
      // separate ability box in the same slot, both charged/granted off
      // the same hit. A slot with an ability but no reward falls straight
      // through to the `!slotDef.reward` guard below and returns there,
      // same as always.
    }
    if (!slotDef.reward) return;
    // Matt reported this directly: "the arrows on deployed cards that
    // allow me to trigger the deployed cards to their left or their
    // right, they don't seem to be activating." "Left/right" means the
    // same thing relative to this player's own board regardless of which
    // side (station or deployed) actually fired it - resolveArrowChain
    // now handles the non-arrow (plain reward) case as a leaf itself, so
    // this always goes through the same chain machinery unconditionally,
    // including secondaryReward (folded into ownBaseRewards uniformly for
    // every hop - see resolveArrowChain's own comment - no separate
    // special-cased grant needed here anymore). Hard Way substitution
    // (resolveHardWay's own comment) happens BEFORE the chain even
    // starts - only ever affects the reward printed on THIS card's own
    // hit, not anything further along an arrow chain (nothing in the
    // rulebook suggests Hard Way propagates through arrows).
    let effectiveSlotDef = slotDef.reward.hardWay
      ? { ...slotDef, reward: resolveHardWay(slotDef.reward, hardWayFace) }
      : slotDef;
    if (!effectiveSlotDef.reward) return;
    // The base game's own l2-aksyonov (doubleStationReward/
    // doubleDeployReward - a pre-existing, never-finished gap) and Terra
    // Proxima's Zarinah ("Double the rewards from cards in this
    // sector.") share the same underlying shape: activating the ability
    // ARMS a one-shot doubling of THIS CARD'S OWN NEXT hit (see
    // useAbility's own doubleStationReward/doubleDeployReward comment for
    // where the arming happens) - consumed here, on the very next time
    // this exact sector+zone is hit for this player, doubling the
    // resource amount (never a text effect's own arrow/pickSectors/etc,
    // which have nothing numeric to double).
    const armed = nextState.meta.armedDoubles?.[playerId] || [];
    const armedIndex = armed.findIndex((a) => a.sector === sectorNum && a.zone === slotKey);
    if (armedIndex !== -1) {
      const doubleReward = (r) => (r && r.kind !== 'text' && r.amount != null ? { ...r, amount: r.amount * 2 } : r);
      effectiveSlotDef = {
        ...effectiveSlotDef,
        reward: doubleReward(effectiveSlotDef.reward),
        secondaryReward: doubleReward(effectiveSlotDef.secondaryReward),
      };
      nextState = {
        ...nextState,
        meta: {
          ...nextState.meta,
          armedDoubles: { ...nextState.meta.armedDoubles, [playerId]: armed.filter((_, i) => i !== armedIndex) },
        },
      };
    }
    const chain = resolveArrowChain(
      [effectiveSlotDef],
      sectorNum,
      slotKey,
      lookupSectorInZone,
      new Set([visitKey(sectorNum, slotKey)]),
    );
    const settled = settleArrowChain(nextState, playerId, chain, sectorNum, instance.instanceId, scoped);
    nextState = settled.state;
    pendingClaims.push(...settled.pendingClaims);
    pendingExtraBuys.push(...settled.pendingExtraBuys);
    pendingSectorPicks.push(...settled.pendingSectorPicks);
    pendingChargeChoices.push(...settled.pendingChargeChoices);
    pendingMoveChoices.push(...settled.pendingMoveChoices);
    pendingCardSwapChoices.push(...settled.pendingCardSwapChoices);
    pendingArrowChoices.push(...settled.pendingArrowChoices);
    pendingEmptySectorChoices.push(...settled.pendingEmptySectorChoices);
  };

  if (isActive) {
    const sector = player.sectors[sectorNum];
    if (sector.station) {
      const def = defFor(sector.station.cardId);
      applySlotEntry(def.station, sector.station, 'station');
    }
  } else {
    const sector = player.sectors[sectorNum];
    sector.deployedStack.forEach((instance) => {
      const def = defFor(instance.cardId);
      applySlotEntry(def.deployed, instance, 'deployed');
    });
  }

  return {
    state: nextState, pendingClaims, pendingExtraBuys, pendingSectorPicks, pendingChargeChoices, pendingMoveChoices, pendingCardSwapChoices, pendingArrowChoices, pendingEmptySectorChoices,
  };
}

// Genesis/Shy Pluto's Bonus Roll icon, word for word from both rulebooks:
// "When you gain a [bonus-roll icon], roll two dice. You allocate the
// dice and gain rewards for this roll like you would as the active
// player. However, any effect that would cause you to affect other
// players, the shipyard, or the unbought colony cards is ignored. You use
// your re-roll abilities and modify your sector rolls with arrow effects
// as normal. Other players ignore your bonus roll - it does not affect
// them." A private roll, entirely separate from the turn's own shared
// state.meta.dice.
//
// Matt reported this directly, twice: "every single decision... needs to
// be blocking. It needs to stop everything and wait for the player to
// make that decision", and specifically against Mary's own 4-roll case:
// "I just sort of rolled a couple times... I didn't ask about splitting
// or not. It needs to do those one at a time and let me choose for each
// roll." Previously auto-resolved to 'sum' whenever both sum/separate
// would actually score - the active roller (and a bonus roll always
// resolves "like the active player") never has an unambiguous choice,
// since every sector always has a station (decideAllocationMode's own
// comment) - a documented, now-resolved tradeoff (docs/expansion-
// decisions-needed.md). Each roll is enqueued here (one entry per
// remaining roll, appended in order - in practice almost always all from
// the same card's own printed `count`) and only ever resolved one at a
// time by resolveBonusRollChoice below, once GameBoard.vue's own UI (or
// the AI's resolveBonusRollForAI) supplies a real mode for the front
// entry - mirrors the main turn roll's own roll-then-allocate shape
// instead of resolving every roll synchronously in one pass. "as normal"
// for re-roll/Modify Die abilities is still resolved atomically within a
// single roll's own applyHit call (a deeper nested-ability pause than
// "which roll's turn is it", not what this fixes) - unchanged.
function enqueueBonusRoll(state, playerId) {
  const [[d1, d2], rngState] = rollTwoDice(state.meta.rngState);
  const values = [d1, d2];
  const hardWayFace = d1 === d2 ? d1 : null;
  // Matt reported this directly: "When I get a bonus roll, I should see
  // the dice roll just like a normal roll." Purely cosmetic "these values
  // were just rolled, for this player" marker - RollDicePopover.vue plays
  // its usual tumble animation off of it (keyed by `seq`, since the same
  // two faces coming up twice in a row is still a real distinct roll),
  // independent of the real pendingBonusRolls entry below, which is what
  // actually keeps it on screen until the player allocates it.
  const lastBonusRoll = { playerId, values, seq: (state.meta.lastBonusRoll?.seq || 0) + 1 };
  return {
    ...state,
    meta: {
      ...state.meta,
      rngState,
      lastBonusRoll,
      pendingBonusRolls: [...(state.meta.pendingBonusRolls || []), { playerId, values, hardWayFace }],
    },
  };
}

// Rulebook (Shy Pluto/Genesis, Bonus Roll): "You use your re-roll
// abilities and modify your sector rolls with arrow effects as normal."
// While a player owns the FRONT pendingBonusRolls entry, their
// allocation-window abilities (reroll / Modify Die / modify-total /
// dice-arrow shift) target THAT roll's own values and modifiers instead
// of the shared meta.dice - see useAbility's own gating and each
// effect's ownsFrontBonusRoll branch. Armed 2X doublings need no special
// casing: applyHit consumes meta.armedDoubles the same way for a bonus
// roll's hits as for a main-roll hit.
export function ownsFrontBonusRoll(state, playerId) {
  return state.meta.pendingBonusRolls?.[0]?.playerId === playerId;
}

export function updateFrontBonusRoll(state, updater) {
  const [front, ...rest] = state.meta.pendingBonusRolls;
  return {
    ...state,
    meta: { ...state.meta, pendingBonusRolls: [updater(front), ...rest] },
  };
}

// Resolves the FRONT entry of pendingBonusRolls - only one is ever "live"
// at a time, same single-at-a-time convention pendingClaims/
// pendingChargeChoices elsewhere already use. `playerId` must match it,
// so a stray dispatch for the wrong player (or with nothing actually
// pending) is a safe no-op rather than resolving someone else's roll.
// `isActive` hardcoded true in the applyHit call below regardless of
// whose turn it actually is - the rulebook's own "gain rewards for this
// roll like you would as the active player" applies to a passive
// player's own bonus roll too.
export function resolveBonusRollChoice(state, { playerId, mode }) {
  const entry = state.meta.pendingBonusRolls?.[0];
  if (!entry || entry.playerId !== playerId) return state;
  // The entry's own modifiers (accumulated by mid-bonus-roll ability use -
  // see ownsFrontBonusRoll above) resolve with the same rules as the main
  // roll: shift/total-modifier only apply to a SUM allocation, die-face
  // adjustments only to a SEPARATE one. hardWayFace derives from the
  // FINAL rolled values (a mid-roll reroll can create or destroy doubles;
  // Modify Die adjustments deliberately don't - they alter an allocation,
  // not what was physically rolled).
  const [v1, v2] = entry.values;
  const hardWayFace = v1 === v2 ? v1 : null;
  let sectors;
  if (mode === 'sum') {
    const shift = entry.sectorShift ? (entry.sectorShift.direction === 'left' ? -entry.sectorShift.shift : entry.sectorShift.shift) : 0;
    const sum = v1 + v2 + shift + (entry.totalModifier || 0);
    sectors = sum >= 2 && sum <= 12 ? [sum] : [];
  } else {
    const [a1, a2] = entry.dieAdjustments || [0, 0];
    const d1 = a1 ? wrapDieFace(v1, a1) : v1;
    const d2 = a2 ? wrapDieFace(v2, a2) : v2;
    sectors = d1 === d2 ? [d1] : [d1, d2];
  }
  let nextState = state;
  const pendingClaims = [];
  const pendingExtraBuys = [];
  const pendingArrowChoices = [];
  const pendingSectorPicks = [];
  const pendingChargeChoices = [];
  const pendingMoveChoices = [];
  const pendingCardSwapChoices = [];
  const pendingEmptySectorChoices = [];
  sectors.forEach((sectorNum) => {
    const result = applyHit(nextState, playerId, sectorNum, true, true, hardWayFace);
    nextState = result.state;
    pendingClaims.push(...result.pendingClaims);
    pendingExtraBuys.push(...result.pendingExtraBuys);
    pendingArrowChoices.push(...result.pendingArrowChoices);
    pendingSectorPicks.push(...result.pendingSectorPicks);
    pendingChargeChoices.push(...result.pendingChargeChoices);
    pendingMoveChoices.push(...result.pendingMoveChoices);
    pendingCardSwapChoices.push(...result.pendingCardSwapChoices);
    pendingEmptySectorChoices.push(...result.pendingEmptySectorChoices);
  });
  return {
    ...nextState,
    meta: {
      ...nextState.meta,
      pendingBonusRolls: (nextState.meta.pendingBonusRolls || []).slice(1),
      pendingClaims: [...(nextState.meta.pendingClaims || []), ...pendingClaims],
      pendingExtraBuys: [...(nextState.meta.pendingExtraBuys || []), ...pendingExtraBuys],
      pendingArrowChoices: [...(nextState.meta.pendingArrowChoices || []), ...pendingArrowChoices],
      pendingSectorPicks: [...(nextState.meta.pendingSectorPicks || []), ...pendingSectorPicks],
      pendingChargeChoices: [...(nextState.meta.pendingChargeChoices || []), ...pendingChargeChoices],
      pendingMoveChoices: [...(nextState.meta.pendingMoveChoices || []), ...pendingMoveChoices],
      pendingCardSwapChoices: [...(nextState.meta.pendingCardSwapChoices || []), ...pendingCardSwapChoices],
      pendingEmptySectorChoices: [...(nextState.meta.pendingEmptySectorChoices || []), ...pendingEmptySectorChoices],
    },
  };
}

// Which sector number(s) a given mode resolves to for this player, given
// the current roll - shared by allocateDice below and, client-side, by
// GameBoard.vue's own "does this player actually have a choice to make"
// check (see playerHasHitAt's own comment).
//
// `diceValues`, when passed explicitly, resolves against THOSE values
// instead of the shared state.meta.dice.values - Genesis/Shy Pluto's
// Bonus Rolls (enqueueBonusRoll below) are a private roll, separate from
// the turn's own shared dice, that still needs this exact sum/separate
// math. Deliberately skips dice.sectorShift in that case too - a
// sum-shifting ability is keyed to the real shared roll (abilities.js's
// own setDice-style effect), and nothing in either rulebook says a bonus
// roll inherits it.
// A die face wraps 1-6 (Terra Proxima's own "Modify Die" rulebook text,
// verified word for word: "If you decrease a die with a 1 result, it
// turns into a 6 result. If you increase a die with a 6 result, it turns
// into a 1 result.").
function wrapDieFace(face, adjustment) {
  return (((face - 1 + adjustment) % 6) + 6) % 6 + 1;
}

export function computeAllocationSectors(state, playerId, mode, diceValues) {
  const { dice } = state.meta;
  const [d1, d2] = diceValues || dice.values;
  if (mode === 'sum') {
    const shift = diceValues ? null : dice.sectorShift?.[playerId];
    const modifier = diceValues ? null : dice.totalModifier?.[playerId];
    let sum = d1 + d2;
    if (shift) sum += shift.direction === 'left' ? -shift.shift : shift.shift;
    if (modifier) sum += modifier;
    return sum >= 2 && sum <= 12 ? [sum] : [];
  }
  // Terra Proxima's "Modify Die" (the Valkyrie Class command ships) -
  // rulebook, verified word for word: "You cannot use this ability if
  // you allocate the sum of the dice" - a physical-component restriction
  // (no separate die tokens left to adjust once combined into one sum)
  // that doesn't have a clean engine equivalent, so it's honored here by
  // simply never reading dieAdjustments in the 'sum' branch above rather
  // than by blocking activation ahead of an allocation choice that
  // hasn't been made yet.
  const adjustments = diceValues ? null : dice.dieAdjustments?.[playerId];
  const [a1, a2] = adjustments || [0, 0];
  const adjustedD1 = a1 ? wrapDieFace(d1, a1) : d1;
  const adjustedD2 = a2 ? wrapDieFace(d2, a2) : d2;
  return adjustedD1 === adjustedD2 ? [adjustedD1, adjustedD1] : [adjustedD1, adjustedD2];
}

// Matt reported this directly: "for all the other players, they only
// really need to make that choice if they have that choice... look at
// what options they have deployed. If they only have one of the two
// options deployed, we should just go ahead and give them the money.
// If they have both options deployed, make them decide. If they have
// nothing deployed... we shouldn't bother [going] to them." A passive
// (non-active) player only gains anything from a DEPLOYED card sitting
// in the hit sector (applyHit's own isActive branch) - the active roller
// always has SOME station in every sector (stations are never empty), so
// this only ever matters for everyone else. GameBoard.vue's own allocate
// flow calls this once per candidate mode (sum, separate) to decide
// whether to prompt the player at all or just auto-submit for them.
// `isActiveOverride`, when passed, skips the normal "am I really this
// turn's active player" derivation - Genesis/Shy Pluto's Bonus Rolls
// (enqueueBonusRoll below) grant rewards "like you would as the active
// player" even when the roller is actually a passive player this turn,
// for that one private roll only.
export function playerHasHitAt(state, playerId, sectorNums, isActiveOverride) {
  const player = state.players.find((p) => p.id === playerId);
  const isActive = isActiveOverride ?? (playerId === state.players[state.meta.activePlayerIndex].id);
  return sectorNums.some((sectorNum) => {
    const sector = player.sectors[sectorNum];
    if (!sector) return false;
    return isActive ? Boolean(sector.station) : sector.deployedStack.length > 0;
  });
}

// Shared by GameBoard.vue's own allocate flow (human players) and
// engine/ai/heuristicBot.js (AI players) - single source of truth for
// "does this player's allocation need a real decision, or can it be
// auto-picked?" so both callers can't drift out of sync on the exact
// same rule. Returns:
//   - 'sum' | 'separate' if only one mode has anything to gain (the
//     obvious pick - or, if NEITHER hits, an arbitrary-but-harmless
//     default, since the mode genuinely doesn't matter then)
//   - null if BOTH modes have something to gain - a real choice, left
//     for the caller (a human via the UI, or the AI via its own scored
//     heuristic in chooseAllocationMode)
//
// `diceValues`/`isActiveOverride` both pass straight through to
// computeAllocationSectors/playerHasHitAt - see their own comments
// (Bonus Rolls need both, for the same private-roll reason).
export function decideAllocationMode(state, playerId, diceValues, isActiveOverride) {
  const sumSectors = computeAllocationSectors(state, playerId, 'sum', diceValues);
  const separateSectors = computeAllocationSectors(state, playerId, 'separate', diceValues);
  const sumHit = playerHasHitAt(state, playerId, sumSectors, isActiveOverride);
  const separateHit = playerHasHitAt(state, playerId, separateSectors, isActiveOverride);
  if (sumHit && separateHit) return null;
  if (!sumHit && !separateHit) return 'sum';
  return sumHit ? 'sum' : 'separate';
}

// mode: 'sum' | 'separate'. Each player (active or passive) independently
// allocates the current roll once per turn (rulebook p12-15).
export function allocateDice(state, playerId, mode) {
  const { dice } = state.meta;
  if (!dice || dice.allocations[playerId]) return state;
  const isActive = playerId === state.players[state.meta.activePlayerIndex].id;
  const sectors = computeAllocationSectors(state, playerId, mode);
  // Hard Way (resolveHardWay's own comment): doubles-ness is a property
  // of the shared physical roll, independent of sum/separate allocation.
  const hardWayFace = dice.values[0] === dice.values[1] ? dice.values[0] : null;

  let nextState = state;
  const allClaims = [];
  const allExtraBuys = [];
  const allArrowChoices = [];
  const allSectorPicks = [];
  const allChargeChoices = [];
  const allMoveChoices = [];
  const allCardSwapChoices = [];
  const allEmptySectorChoices = [];
  sectors.forEach((sectorNum) => {
    const result = applyHit(nextState, playerId, sectorNum, isActive, false, hardWayFace);
    nextState = result.state;
    allClaims.push(...result.pendingClaims);
    allExtraBuys.push(...result.pendingExtraBuys);
    allArrowChoices.push(...result.pendingArrowChoices);
    allSectorPicks.push(...result.pendingSectorPicks);
    allChargeChoices.push(...result.pendingChargeChoices);
    allMoveChoices.push(...result.pendingMoveChoices);
    allCardSwapChoices.push(...result.pendingCardSwapChoices);
    allEmptySectorChoices.push(...result.pendingEmptySectorChoices);
  });

  nextState = {
    ...nextState,
    meta: {
      ...nextState.meta,
      dice: { ...nextState.meta.dice, allocations: { ...nextState.meta.dice.allocations, [playerId]: mode } },
      pendingClaims: [...(nextState.meta.pendingClaims || []), ...allClaims],
      pendingExtraBuys: [...(nextState.meta.pendingExtraBuys || []), ...allExtraBuys],
      pendingArrowChoices: [...(nextState.meta.pendingArrowChoices || []), ...allArrowChoices],
      pendingSectorPicks: [...(nextState.meta.pendingSectorPicks || []), ...allSectorPicks],
      pendingChargeChoices: [...(nextState.meta.pendingChargeChoices || []), ...allChargeChoices],
      pendingMoveChoices: [...(nextState.meta.pendingMoveChoices || []), ...allMoveChoices],
      pendingCardSwapChoices: [...(nextState.meta.pendingCardSwapChoices || []), ...allCardSwapChoices],
      pendingEmptySectorChoices: [...(nextState.meta.pendingEmptySectorChoices || []), ...allEmptySectorChoices],
    },
  };

  // Biodome's Thrivax ("Each roll when you use the sum of the dice,
  // after gaining rewards you may also gain rewards for one die.") -
  // queued after the sum rewards above, a genuine pick between the two
  // individual die sectors (one option when doubles). Optional ("may"),
  // so the choice is declinable.
  const chooser = nextState.players.find((p) => p.id === playerId);
  if (mode === 'sum' && chooser && ownsOngoingModifier(chooser, 'sumPlusOneDie')) {
    const options = [...new Set(nextState.meta.dice.values)];
    nextState = {
      ...nextState,
      meta: {
        ...nextState.meta,
        pendingDieRewardChoices: [
          ...(nextState.meta.pendingDieRewardChoices || []),
          { playerId, options, isActive },
        ],
      },
    };
  }

  const allResolved = nextState.players.every((p) => nextState.meta.dice.allocations[p.id]);
  if (allResolved) {
    nextState = { ...nextState, meta: { ...nextState.meta, turnPhase: 'post-reward' } };
  }
  return nextState;
}

// Rulebook (base game, verified word for word, p.12-13 "How to Play" +
// p.23-27 "Ability Clarifications"/"Reference"): most ability cards can
// be triggered "during phase 1, 5, or 7" of the active player's turn -
// i.e. any of the three windows abilities may be triggered, OUTSIDE the
// actual roll/allocate/reward sequence itself. That's this engine's own
// pre-roll/post-reward/post-buy turnPhase values.
const ABILITY_WINDOW_PHASES = ['pre-roll', 'post-reward', 'post-buy'];
// A second, narrower family is instead scoped to the brief window WHILE
// dice already exist for this roll but haven't all been resolved into
// rewards yet (this engine's own 'awaiting-allocation'): Reroll Dice
// ("phase 2 ('roll')... rerolling affects all players"), the two "2X"
// abilities ("phase 4 ('gain rewards')... BEFORE gaining rewards for the
// roll"), and the dice-arrow-shift/Modify Die/Modify Dice Total family
// ("phase 3 ('allocate dice')... only if you chose the sum of the
// dice" - the sum-only half of that rule is enforced downstream instead,
// by computeAllocationSectors never reading these fields on a 'separate'
// allocation - see modifyDieFace's own comment - not by blocking
// activation here).
const ALLOCATION_WINDOW_EFFECT_KINDS = [
  'rerollDice', 'doubleStationReward', 'doubleDeployReward', 'modifyDiceTotal', 'modifyDieFace', 'diceArrowShift',
];
// Set Dice is the one ability restricted to BEFORE the roll even exists
// at all ("must be used before you roll the dice") - the ONLY effect
// kind that needs turnPhase 'pre-roll' specifically, not the general
// phase-1/5/7 window (which would also wrongly allow it post-reward/
// post-buy, long after this turn's own roll already happened).
const PRE_ROLL_ONLY_EFFECT_KINDS = ['setDice'];

export function useAbility(state, playerId, instanceId, slotKey, params = {}) {
  const player = state.players.find((p) => p.id === playerId);
  const found = findInstance(player, instanceId);
  if (!found) return state;
  const def = defFor(found.instance.cardId);
  const slotDef = slotKey === 'station' ? def.station : def.deployed;
  const ability = slotDef?.ability;
  if (!ability) return state;

  const isActive = playerId === state.players[state.meta.activePlayerIndex].id;
  const timingOk = (
    (ability.timing === 'blue' && isActive)
    || (ability.timing === 'red' && !isActive)
    || ability.timing === 'green'
  );
  if (!timingOk) return state;
  const effectKind = ability.effect.kind;
  const phaseOk = PRE_ROLL_ONLY_EFFECT_KINDS.includes(effectKind)
    ? state.meta.turnPhase === 'pre-roll'
    : ALLOCATION_WINDOW_EFFECT_KINDS.includes(effectKind)
      // "You use your re-roll abilities and modify your sector rolls with
      // arrow effects as normal" during a Bonus Roll (rulebook) - a
      // player's own pending bonus roll opens the same window their main
      // roll's awaiting-allocation phase does.
      ? state.meta.turnPhase === 'awaiting-allocation' || ownsFrontBonusRoll(state, playerId)
      : ABILITY_WINDOW_PHASES.includes(state.meta.turnPhase);
  if (!phaseOk) return state;
  if (!isAbilityCharged(found.instance, slotKey, ability, state.players.length)) return state;

  // Shy Pluto's own "Trumbull Class Research Station" ships (Reiter etc.)
  // - Matt's own explanation: up to 4 charges can be banked as 2
  // independent pairs, and spending only consumes the 2 actually
  // required, leaving any banked remainder alone rather than wiping the
  // whole pool - `repeatable` on the ability signals this to
  // spendCharges. Every other ability keeps the normal "reset straight
  // to 0" behavior.
  const spendAmount = ability.repeatable ? chargesRequired(ability, state.players.length) : null;
  let nextState = replacePlayer(state, playerId, (p) => spendCharges(p, instanceId, slotKey, spendAmount));

  if (ability.effect.kind === 'buyCard') {
    nextState = {
      ...nextState,
      meta: { ...nextState.meta, pendingExtraBuys: [...(nextState.meta.pendingExtraBuys || []), { playerId, free: false, sectorRange: null }] },
    };
  } else if (ability.effect.kind === 'buyCardSectorRange') {
    nextState = {
      ...nextState,
      meta: {
        ...nextState.meta,
        pendingExtraBuys: [...(nextState.meta.pendingExtraBuys || []), {
          playerId, free: false, sectorRange: [ability.effect.minSector, ability.effect.maxSector],
        }],
      },
    };
  } else if (ability.effect.kind === 'buyCardDiscount') {
    // Terra Proxima's Stirling ("...you may buy a card for 3 less.") - a
    // discounted (not free) extra buy, same shape as buyCard's own
    // pendingExtraBuys entry but with a flat amount off the cost -
    // buyCard's own ship branch checks `extraBuy.discount` alongside its
    // existing free/sectorRange handling.
    nextState = {
      ...nextState,
      meta: {
        ...nextState.meta,
        pendingExtraBuys: [...(nextState.meta.pendingExtraBuys || []), {
          playerId, free: false, sectorRange: null, discount: ability.effect.amount,
        }],
      },
    };
  } else if (ability.effect.kind === 'buyColonyHalfCost') {
    // Genesis's Haumea ("Buy a colony card for half the cost rounded
    // up.") - colony-ONLY (buyCard's own ship branch rejects
    // `colonyOnly`), not free - `colonyHalfCost` is buyCard's own colony
    // branch signal to round the printed cost up/2 instead of charging
    // it in full.
    nextState = {
      ...nextState,
      meta: {
        ...nextState.meta,
        pendingExtraBuys: [...(nextState.meta.pendingExtraBuys || []), {
          playerId, free: false, sectorRange: null, colonyOnly: true, colonyHalfCost: true,
        }],
      },
    };
  } else if (ability.effect.kind === 'buyColonyDiscount') {
    // Terra Proxima's U.E.C. Genesis 0036-A ("Colony cards cost you 9
    // less.") - same colony-ONLY shape as Haumea's own buyColonyHalfCost,
    // but a flat amount off (like a ship's own buyCardDiscount) instead
    // of half cost.
    nextState = {
      ...nextState,
      meta: {
        ...nextState.meta,
        pendingExtraBuys: [...(nextState.meta.pendingExtraBuys || []), {
          playerId, free: false, sectorRange: null, colonyOnly: true, colonyDiscount: ability.effect.amount,
        }],
      },
    };
  } else if (ability.effect.kind === 'modifyDiceTotal') {
    // Genesis's Pegasus - its own printed text is "increase the total of
    // both dice, for just yourself, by 1/2/3" (a genuinely different,
    // bespoke effect from Terra Proxima's own official "Modify Die"
    // ability just below, despite the similar theme - Pegasus adjusts
    // the derived SUM directly, not an individual die face). Same
    // mechanism the base game's own `diceArrowShift` already uses
    // (per-player, stored on state.meta.dice, only read back by
    // computeAllocationSectors' own 'sum' branch) - kept as a SEPARATE
    // field (`totalModifier`, not `sectorShift`) so a player who somehow
    // has both a shift and a modifier active the same roll gets both
    // added, rather than one silently overwriting the other. Only
    // meaningful once dice actually exist for this roll (awaiting-
    // allocation) - firing earlier (legal per ABILITY_WINDOW_PHASES,
    // e.g. pre-roll) just wastes the charge, same accepted shape as
    // exchangeCard's own missing-target case.
    {
      const requested = params?.amount ?? ability.effect.min;
      const amount = Math.min(Math.max(requested, ability.effect.min), ability.effect.max);
      if (ownsFrontBonusRoll(nextState, playerId)) {
        // Applies to this player's own pending Bonus Roll, not the shared
        // turn dice (rulebook's "as normal" - see ownsFrontBonusRoll).
        nextState = updateFrontBonusRoll(nextState, (e) => ({ ...e, totalModifier: (e.totalModifier || 0) + amount }));
      } else if (nextState.meta.turnPhase === 'awaiting-allocation') {
        const dice = nextState.meta.dice || {};
        const mods = { ...(dice.totalModifier || {}) };
        mods[playerId] = (mods[playerId] || 0) + amount;
        nextState = { ...nextState, meta: { ...nextState.meta, dice: { ...dice, totalModifier: mods } } };
      }
    }
  } else if (ability.effect.kind === 'modifyDieFace') {
    // Terra Proxima's own official "Modify Die" ability (Valkyrie Class
    // Fast Response Command Ships - Gavryn/Enders/Larris/Darinell/
    // Camdon/Shaene) - rulebook, verified word for word: "You may modify
    // one die by either increasing (+) or decreasing (-) the result by
    // one" (wrapping 1<->6), "can be used... multiple times during the
    // same turn with multiple uses of this effect" (accumulates, doesn't
    // overwrite), and "cannot use this ability if you allocate the sum
    // of the dice" (honored by computeAllocationSectors' own 'sum'
    // branch never reading dieAdjustments at all, rather than blocking
    // activation ahead of an allocation choice the engine hasn't
    // resolved yet at this point - a documented simplification, see
    // docs/expansion-decisions-needed.md). `params.dieIndex` (0 or 1)
    // and `params.direction` (+1 or -1) default to die 0 / +1 for a
    // generic activation (the AI's own tryUseReadyAbilities never
    // supplies params) - genuinely correct strategy here needs to know
    // which allocation would actually help, well beyond this codebase's
    // existing "try anything legal" AI heuristics.
    {
      const dieIndex = params?.dieIndex === 1 ? 1 : 0;
      const direction = params?.direction === -1 ? -1 : 1;
      if (ownsFrontBonusRoll(nextState, playerId)) {
        nextState = updateFrontBonusRoll(nextState, (e) => {
          const next = [...(e.dieAdjustments || [0, 0])];
          next[dieIndex] += direction;
          return { ...e, dieAdjustments: next };
        });
      } else if (nextState.meta.turnPhase === 'awaiting-allocation') {
        const dice = nextState.meta.dice || {};
        const adjustments = { ...(dice.dieAdjustments || {}) };
        const current = adjustments[playerId] || [0, 0];
        const next = [...current];
        next[dieIndex] += direction;
        adjustments[playerId] = next;
        nextState = { ...nextState, meta: { ...nextState.meta, dice: { ...dice, dieAdjustments: adjustments } } };
      }
    }
  } else if (ability.effect.kind === 'doubleStationReward' || ability.effect.kind === 'doubleDeployReward') {
    // Base game's l2-aksyonov and Terra Proxima's Zarinah ("Double the
    // rewards from cards in this sector.") - a pre-existing gap (the
    // ability charged/activated but the actual doubling was never wired
    // up). Activating ARMS a one-shot doubling of THIS CARD'S OWN sector
    // (`found.sectorNum`), for the zone the printed effect names (station
    // or deployed - NOT necessarily the same zone the ability itself
    // sits on, since a station ability could double either side
    // depending on which effect kind is printed) - consumed by
    // applySlotEntry's own armedDoubles check on the very next hit there,
    // see its own comment. Multiple armed entries can coexist (e.g. two
    // different cards arming two different sectors), so this appends
    // rather than overwrites.
    const doubledZone = ability.effect.kind === 'doubleStationReward' ? 'station' : 'deployed';
    const armed = nextState.meta.armedDoubles || {};
    const playerArmed = armed[playerId] || [];
    nextState = {
      ...nextState,
      meta: {
        ...nextState.meta,
        armedDoubles: { ...armed, [playerId]: [...playerArmed, { sector: found.sectorNum, zone: doubledZone }] },
      },
    };
  } else if (ability.effect.kind === 'triggerZoneReward') {
    // Terra Proxima's Cornelius/Zaius (Stargazer Class Relay Station) -
    // Matt's own explanation: the station side's plain reward is the
    // usual same-sector crossZone directional arrow (already fully
    // supported as a plain per-hit reward, no ability involved there);
    // the DEPLOYED side is different - it takes 2 charges, and spending
    // them "triggers the blue [station] reward in the same sector",
    // requiring you to be the active roller (`timing: 'blue'`, matching
    // the card's own printed blue box) rather than firing automatically.
    // Resolved by literally reusing the same arrow-chain machinery a
    // real dice hit already goes through (ownSectorSlotDefs/
    // resolveArrowChain/settleArrowChain), just triggered by an ability
    // activation instead of a roll - `ability.effect.zone` is the target
    // zone to resolve as if just hit ('station' for Cornelius/Zaius).
    {
      const sectorNum = found.sectorNum;
      const targetZone = ability.effect.zone;
      const lookupZone = (targetSector, zone) => ownSectorSlotDefs(nextState, playerId, targetSector, zone);
      const slotDefs = lookupZone(sectorNum, targetZone);
      const chain = resolveArrowChain(slotDefs, sectorNum, targetZone, lookupZone, new Set([visitKey(sectorNum, targetZone)]));
      const settled = settleArrowChain(nextState, playerId, chain, sectorNum, instanceId, false);
      nextState = {
        ...settled.state,
        meta: {
          ...settled.state.meta,
          pendingClaims: [...(settled.state.meta.pendingClaims || []), ...settled.pendingClaims],
          pendingExtraBuys: [...(settled.state.meta.pendingExtraBuys || []), ...settled.pendingExtraBuys],
          pendingSectorPicks: [...(settled.state.meta.pendingSectorPicks || []), ...settled.pendingSectorPicks],
          pendingChargeChoices: [...(settled.state.meta.pendingChargeChoices || []), ...settled.pendingChargeChoices],
          pendingMoveChoices: [...(settled.state.meta.pendingMoveChoices || []), ...settled.pendingMoveChoices],
          pendingCardSwapChoices: [...(settled.state.meta.pendingCardSwapChoices || []), ...settled.pendingCardSwapChoices],
          pendingArrowChoices: [...(settled.state.meta.pendingArrowChoices || []), ...settled.pendingArrowChoices],
          pendingEmptySectorChoices: [...(settled.state.meta.pendingEmptySectorChoices || []), ...settled.pendingEmptySectorChoices],
        },
      };
    }
  } else if (ability.effect.kind === 'bonusRoll') {
    // Shy Pluto's Bennell/Thade/Mandemis/Quaritch (deployed) - the SAME
    // private "Bonus Roll" a plain text reward can already grant
    // (enqueueBonusRoll), just gated behind a chargeable ability instead
    // of firing automatically on a hit. `effect.reward`, when present
    // (Bennell's own "roll AND gain 1 income" - both icons stacked in
    // the same payout box, confirmed with Matt), is granted IN ADDITION
    // to the roll, not instead of it.
    nextState = enqueueBonusRoll(nextState, playerId);
    if (ability.effect.reward) {
      nextState = applyResourceToPlayer(nextState, playerId, ability.effect.reward);
    }
  } else if (ability.effect.kind === 'rerollDice') {
    if (ownsFrontBonusRoll(nextState, playerId)) {
      // Reroll the player's own pending Bonus Roll dice instead of the
      // shared turn dice (rulebook's "as normal"). A reroll replaces the
      // physically rolled faces, so hard-way doubles are re-derived from
      // the new values at resolve time (resolveBonusRollChoice).
      const [d1, r1] = rollDie(nextState.meta.rngState);
      if (ability.effect.dice === 'both') {
        const [d2, r2] = rollDie(r1);
        nextState = { ...nextState, meta: { ...nextState.meta, rngState: r2 } };
        nextState = updateFrontBonusRoll(nextState, (e) => ({ ...e, values: [d1, d2] }));
      } else {
        nextState = { ...nextState, meta: { ...nextState.meta, rngState: r1 } };
        nextState = updateFrontBonusRoll(nextState, (e) => ({ ...e, values: [d1, e.values[1]] }));
      }
    } else {
      nextState = rerollDice(nextState, ability.effect.dice === 'both' ? null : 0);
    }
  } else if (ability.effect.kind === 'claimCard') {
    // Genesis's own "claim card" Tug-class family (Kovalyonok/Nikolayev/
    // Bykovsky/Popovich/Tereshkova) - Matt's own direct explanation:
    // "you add a charge on someone else's turn and can spend that charge
    // on your turn to claim [the card]." Re-verified against the card
    // art after he pointed out the small charge-square graphic on these
    // cards' own deployed box (and Kovalyonok's station box too) - the
    // original transcription read every one of these as a plain
    // per-hit reward (grantReward's own claimCard case, still used by
    // any future card that really is a plain claim with no charge
    // square), missing the charge gate entirely. Same pendingClaims
    // shape either way, just reached via useAbility instead of a direct
    // hit.
    nextState = {
      ...nextState,
      meta: {
        ...nextState.meta,
        pendingClaims: [...(nextState.meta.pendingClaims || []), ...ability.effect.claims.map((c) => ({ ...c, playerId }))],
      },
    };
  } else if (ability.effect.kind === 'peekAndClaim') {
    // Genesis's Dalton, hypothetically as a chargeable ability (the real
    // card is actually a plain one-time reward - see grantReward's own
    // peekAndClaim case) - kept here too in case a future card DOES gate
    // this behind charges.
    nextState = queueDeckPeek(nextState, playerId, ability.effect.level, ability.effect.count);
  } else {
    // `sourceInstanceId` (Nikola's own "all your OTHER cards" - see
    // abilities.js's own placeChargeAll case) lets an effect exclude the
    // very card that granted it, without every other effect kind needing
    // to know or care about this extra field.
    nextState = applyAtomicEffect(nextState, playerId, ability.effect, { ...params, sourceInstanceId: instanceId });
  }
  // Gordon (l3-gordon), verified against the card scan (2026-08-18 backlog
  // item 3): station "All opponents lose 4. Buy one or two cards." /
  // deployed "All opponents lose 3. Buy a card." The buy half rides along
  // the main effect as effect.extraBuys - N ordinary exact-cost extra buys
  // (rulebook p24: "you only spend the exact cost of the card you buy"),
  // each individually declinable ("you are not required to buy a card"),
  // which is what makes the printed "one or two" a real choice.
  if (ability.effect.extraBuys) {
    const buys = Array.from({ length: ability.effect.extraBuys }, () => ({ playerId, free: false, sectorRange: null }));
    nextState = {
      ...nextState,
      meta: { ...nextState.meta, pendingExtraBuys: [...(nextState.meta.pendingExtraBuys || []), ...buys] },
    };
  }
  return nextState;
}

function refillDisplaySlot(state, level, cardId) {
  const display = state.shipyard.display[level].filter((id) => id !== cardId);
  const draw = state.shipyard.draw[level].slice();
  const drawn = draw.shift();
  if (drawn) display.push(drawn);
  return {
    ...state,
    shipyard: {
      ...state.shipyard,
      display: { ...state.shipyard.display, [level]: display },
      draw: { ...state.shipyard.draw, [level]: draw },
    },
  };
}

// Matt reported this directly: "I was just able to purchase a level two
// card even though I had already put a colony in my level two slot. The
// colony was deployed. This should not be allowed once you have a
// colony in a slot." Colonies (colonyCards.js's own comment: "Colonies
// are always available face-up, never refilled/replaced once bought")
// have no station/deployed sub-object at all - identifying one this way
// (rather than string-matching the "colony-" id prefix) works for any
// future colony-shaped card too, not just this specific set.
// Biodome's ongoing colony modifiers (buyOneSectorHigher / sumPlusOneDie /
// buyOtherPlayersColonyReward / mirrorDice / buyShipsDeployed /
// claimBeforeRoll) are DERIVED from board state, never stored: a colony
// can't be displaced once bought, so "does this player's board hold the
// card" is the modifier's entire lifecycle - no activation record, no
// expiry, nothing to migrate.
export function ownsOngoingModifier(player, name) {
  return Object.values(player.sectors).some((s) => (
    s.station && defFor(s.station.cardId).ongoingModifier === name
  ));
}

export function sectorHasColony(player, sector) {
  const stationCardId = player.sectors[sector]?.station?.cardId;
  return Boolean(stationCardId) && !defFor(stationCardId).station;
}

// Genesis's "Claimed" colonies (Akey/Harbison/Mara/Bongiu, rulebook's own
// red-text free-claim family: "On your turn, you may claim this card if
// [board-state condition]." - NOT a purchase, cost is always 0) - a
// board-state eligibility GATE rather than a whole new acquisition path:
// these still flow through buyCard's own normal colony branch (deployCard,
// vp grant, colonies-taken marking all unchanged), just with `cost: 0` on
// the card itself and this check standing in for "can afford it".
export function meetsClaimCondition(player, condition) {
  if (!condition) return true;
  switch (condition.kind) {
    // Akey: "at least 1 deployed card in every sector."
    case 'deployedInEverySector':
      return SECTORS.every((s) => (player.sectors[s]?.deployedStack.length || 0) >= 1);
    // Harbison: "at least 9 charges on your cards" - every station AND
    // deployed charge cube currently on the board, summed.
    case 'totalCharges': {
      const total = Object.values(player.sectors).reduce((sum, sector) => {
        const stationCharges = sector.station?.stationCharges || 0;
        const deployedCharges = sector.deployedStack.reduce((a, d) => a + d.deployedCharges, 0);
        return sum + stationCharges + deployedCharges;
      }, 0);
      return total >= condition.amount;
    }
    // Mara: "at least 14 income."
    case 'income':
      return player.income >= condition.amount;
    // Bongiu: "a sector with at least 7 deployed cards" - any ONE sector,
    // not the total across all of them (distinct from totalCharges above).
    case 'sectorDeployedCount':
      return Object.values(player.sectors).some((sector) => sector.deployedStack.length >= condition.amount);
    default:
      return true;
  }
}

function deployCard(state, playerId, def, sectorOverride) {
  const sector = sectorOverride || def.sector;
  return replacePlayer(state, playerId, (p) => {
    const target = p.sectors[sector];
    const bumped = target.station;
    // Genesis's own "Empty Sectors" rule (rulebook p5): some effects
    // empty a station outright (see emptySector's own comment below), so
    // a purchase into that sector may have NOTHING to bump - "you may
    // buy a new card to place in an empty sector as normal," no carried
    // charges since there's no prior occupant at all.
    // Matt reported this directly: "when a charge is on a card and that
    // card gets deployed, the charge also moves to the deployed side if
    // there's a spot for it." A physical charge cube already on the
    // card's station squares carries over onto its own deployed squares
    // when it flips, capped at however many are actually printed there -
    // no cube, no deployed ability at all, and any charges beyond the
    // deployed side's own count just don't have anywhere to go.
    const bumpedDeployedAbility = bumped ? defFor(bumped.cardId).deployed?.ability : null;
    const carriedCharges = bumpedDeployedAbility
      ? Math.min(bumped.stationCharges, bumpedDeployedAbility.squares)
      : 0;
    return {
      ...p,
      sectors: {
        ...p.sectors,
        [sector]: {
          station: makeInstance(def.id),
          deployedStack: bumped ? [...target.deployedStack, { ...bumped, deployedCharges: carriedCharges }] : target.deployedStack,
        },
      },
    };
  });
}

// Genesis's own "Empty Sectors" rule (rulebook p5, verified word for
// word): "Some effects deploy or discard cards on your board, resulting
// in an empty sector. You do not gain any rewards for allocating dice to
// an empty sector on your turn. You may buy a new card to place in an
// empty sector as normal." Pharos (station: "Deploy all your ship cards
// in sectors 7-12", deployed: "Deploy any 1 ship card") is the only card
// that actually empties a sector this way - this just moves the current
// station occupant (if any) into its own deployedStack, same
// charge-carryover math deployCard's own bump already uses, leaving
// `station: null` behind. `stationRewardsForSector` (rewards.js) already
// returns a safe no-reward shape for a null station, so allocating dice
// there already correctly grants nothing - no changes needed on that
// side of the rule.
function emptySector(state, playerId, sector) {
  return replacePlayer(state, playerId, (p) => {
    const target = p.sectors[sector];
    if (!target?.station) return p;
    const bumped = target.station;
    const bumpedDeployedAbility = defFor(bumped.cardId).deployed?.ability;
    const carriedCharges = bumpedDeployedAbility
      ? Math.min(bumped.stationCharges, bumpedDeployedAbility.squares)
      : 0;
    return {
      ...p,
      sectors: {
        ...p.sectors,
        [sector]: {
          station: null,
          deployedStack: [...target.deployedStack, { ...bumped, deployedCharges: carriedCharges }],
        },
      },
    };
  });
}

// Genesis's own claim/colony interaction (rulebook p5, verified word for
// word): "If you claim a ship and already have a colony in its sector,
// the ship is immediately deployed instead of being placed on your
// board." Unlike deployCard above, nothing gets bumped here - a colony
// is a permanent station occupant (the same sectorHasColony check blocks
// a normal BUY into that sector entirely, base game rules), so the
// claimed ship goes straight onto the deployed stack alongside it,
// station left untouched.
function deployCardIntoColonizedSector(state, playerId, def, sector) {
  return replacePlayer(state, playerId, (p) => ({
    ...p,
    sectors: {
      ...p.sectors,
      [sector]: { ...p.sectors[sector], deployedStack: [...p.sectors[sector].deployedStack, makeInstance(def.id)] },
    },
  }));
}

// { level, cardId } for a shipyard card, or { colonyCardId } for a colony.
// extraBuyIndex: consumes state.meta.pendingExtraBuys[extraBuyIndex] instead
// of the once-per-turn normal buy, when set. A normal buy is always the
// active player's own (that's inherent to the post-reward phase, no
// playerId needed from the caller) - but an extra buy can belong to a
// PASSIVE player too (e.g. l2-kovalyonok's deployed "claim one level 2
// card" text, or a red-timed buyCardSectorRange ability triggering off
// someone else's roll), so playerId IS required and validated against
// extraBuy.playerId on that path.
export function buyCard(state, {
  playerId, level, cardId, colonyCardId, sectorOverride, extraBuyIndex, colonyChoice, colonyOrZone, colonyPairIndex, colonyTargetPlayerId, colonyChosenNumber, colonyTargetSector, deployImmediately, ionTapColonyCardId,
} = {}) {
  const activePlayer = state.players[state.meta.activePlayerIndex];
  const usingExtraBuy = extraBuyIndex != null;
  const extraBuy = usingExtraBuy ? state.meta.pendingExtraBuys[extraBuyIndex] : null;
  if (usingExtraBuy && (!extraBuy || extraBuy.playerId !== playerId)) return state;
  if (!usingExtraBuy && state.meta.turnPhase !== 'post-reward') return state;
  const buyer = usingExtraBuy ? state.players.find((p) => p.id === extraBuy.playerId) : activePlayer;
  if (!buyer) return state;

  let nextState = state;

  // Biodome's Ion Outpost ("As a buy on your turn you may pay the cost
  // of a colony card on another player's board to gain the immediate
  // (yellow) reward of that colony.") - a genuine third KIND of buy: the
  // buyer spends their turn's buy and the colony's printed cost, gains
  // that colony's own top (yellow) reward, and the card itself never
  // moves. Ordinary buys only (never an extra buy - the printed text is
  // "as a buy on your turn"), gated on actually owning Ion.
  if (ionTapColonyCardId) {
    if (usingExtraBuy) return state;
    if (!ownsOngoingModifier(buyer, 'buyOtherPlayersColonyReward')) return state;
    const owner = state.players.find((p) => (
      p.id !== buyer.id && Object.values(p.sectors).some((s) => s.station?.cardId === ionTapColonyCardId)
    ));
    if (!owner) return state;
    const def = defFor(ionTapColonyCardId);
    if (buyer.credits < def.cost) return state;
    // A normal buy always spends the whole credit cube (same rule as any
    // other purchase in this function).
    const keepsUnspent = (nextState.meta.keepsUnspentCreditsPlayerIds || []).includes(buyer.id);
    nextState = replacePlayer(nextState, buyer.id, (p) => ({ ...p, credits: keepsUnspent ? p.credits - def.cost : 0 }));
    if (def.vp != null) {
      nextState = applyResourceToPlayer(nextState, buyer.id, { kind: 'vp', amount: def.vp, source: 'colony' });
    }
    return { ...nextState, meta: { ...nextState.meta, turnPhase: 'post-buy' } };
  }

  if (colonyCardId) {
    const colony = nextState.colonies.find((c) => c.cardId === colonyCardId);
    if (!colony || colony.taken) return state;
    const def = defFor(colonyCardId);
    // Genesis's Holcomb ("You gain 6 vp, then place this card on ANOTHER
    // PLAYER's board in their sector 5 instead of your own board.") - the
    // card doesn't say which other player, so (documented in
    // docs/expansion-decisions-needed.md) this reads it as the buyer's
    // own choice, same as any other "choose a player" text elsewhere.
    // Falls back to the buyer themselves if no valid target was passed,
    // so a stray/omitted param can't silently target nobody.
    const colonyOwnerId = (def.deployToOtherPlayer && colonyTargetPlayerId && colonyTargetPlayerId !== buyer.id
      && state.players.some((p) => p.id === colonyTargetPlayerId)) ? colonyTargetPlayerId : buyer.id;
    const colonyOwner = state.players.find((p) => p.id === colonyOwnerId);
    // Genesis's "Claimed" colonies (Akey/Harbison/Mara/Bongiu) - a free
    // claim gated on a board-state condition instead of the usual
    // credits check (their own printed cost is 0, so the normal credits
    // guard just below is already a no-op for these; this is the actual
    // gate). Checked against the BUYER, not colonyOwner - Holcomb's own
    // other-player redirect never applies to a Claimed colony (no
    // existing card combines the two), so buyer is unambiguously correct.
    if (def.claimCondition && !meetsClaimCondition(buyer, def.claimCondition)) return state;
    // Defensive - each colony's sector is unique (one colony per sector
    // 1-12, colonyCards.js's own comment) so this shouldn't be reachable
    // via colony.taken alone, but a sector already holding SOME OTHER
    // colony must stay blocked the same as any other purchase would be.
    // Checked against whoever will actually RECEIVE the card (Holcomb's
    // own target, ordinarily the buyer).
    // Biodome's Circarth ("You may place this card in any sector.") -
    // an explicit in-range colonyTargetSector overrides the printed
    // sector; every other colony ignores the param entirely.
    const colonySector = (def.colonyAnySector && colonyTargetSector >= 1 && colonyTargetSector <= 12)
      ? colonyTargetSector
      : def.sector;
    if (sectorHasColony(colonyOwner, colonySector)) return state;
    // Genesis's Haumea ("Buy a colony card for half the cost rounded
    // up." - a printed ability, not free) - the only currently-existing
    // colony extraBuy is Genesis's own free `buyCard` ability, so this is
    // the first NON-free one; `colonyHalfCost` rounds up per the card's
    // own wording (Math.ceil), same idea as a ship's own flat `discount`
    // (useAbility's own buyCardDiscount comment) but proportional instead
    // of a flat amount off. Terra Proxima's genesis-ship uses the flat
    // form instead (`colonyDiscount`), floored at 0 same as a ship's own
    // discount.
    let cost = def.cost;
    if (usingExtraBuy && extraBuy.colonyHalfCost) cost = Math.ceil(def.cost / 2);
    else if (usingExtraBuy && extraBuy.colonyDiscount) cost = Math.max(0, def.cost - extraBuy.colonyDiscount);
    if (!usingExtraBuy && buyer.credits < cost) return state;
    if (usingExtraBuy && !extraBuy.free && buyer.credits < cost) return state;
    // Genesis's Zinser ("When you buy this card, gain 1 vp for each
    // excess 5 credits spent.") - a normal buy always spends the WHOLE
    // credit cube (base game rule), so "excess spent" is simply whatever
    // the buyer had beyond this card's own cost, captured here before
    // that cube gets zeroed out below.
    const excessCredits = (!usingExtraBuy || !extraBuy.free) ? Math.max(0, buyer.credits - cost) : 0;
    if (!usingExtraBuy) {
      // Genesis's "keep unspent credits" ability (Rogue Star/Iron Dragon/
      // Black Diamond - see abilities.js's own keepUnspentCredits case):
      // a normal buy ALWAYS spends the whole credit cube to 0, UNLESS
      // this player activated that ability this turn, in which case only
      // the actual cost comes off - same as a non-free extra buy already
      // does below.
      const keepsUnspent = (nextState.meta.keepsUnspentCreditsPlayerIds || []).includes(buyer.id);
      nextState = replacePlayer(nextState, buyer.id, (p) => ({ ...p, credits: keepsUnspent ? p.credits - cost : 0 }));
    } else if (!extraBuy.free) {
      nextState = replacePlayer(nextState, buyer.id, (p) => ({ ...p, credits: p.credits - cost }));
    }
    nextState = deployCard(nextState, colonyOwnerId, def, colonySector);
    // Genesis's Peterschmidt colony (rulebook Card 114, verified word for
    // word: "the game just ends and you win") prints NO vp at all - its
    // own text IS "YOU WIN" instead, reusing the existing `youWin`
    // ability effect's own instantWinnerId mechanism unchanged, so
    // def.vp is only granted when the card actually has one.
    //
    // Several colonies print a genuine buy-time "OR" choice (Carman:
    // "gain 8 vp OR gain 60 credits"; Tzioti/Wood/etc: "OR gain your
    // blue/red rewards for sectors X-Y"; Livingston/Zimmerman: "OR gain
    // ALL your red/blue rewards") - `colonyChoice: 'or'` (defaulting to
    // the plain vp grant when omitted, same as "you don't have to take
    // the alternate") resolves `def.orChoice` INSTEAD of `def.vp`.
    // `colonyOrZone` ('station'|'deployed') is only consulted for
    // Livingston's own genuine zone choice (`orChoice.zoneChoice: true`)
    // - every other orChoice already has a fixed zone (or, for
    // Zimmerman's "AND", both).
    if (colonyChoice === 'or' && def.orChoice) {
      if (def.orChoice.kind === 'flatResource') {
        nextState = applyResourceToPlayer(nextState, buyer.id, def.orChoice.reward);
      } else if (def.orChoice.kind === 'sectorRangeReward') {
        const zones = def.orChoice.zoneChoice
          ? [colonyOrZone === 'deployed' ? 'deployed' : 'station']
          : (def.orChoice.zone === 'both' ? ['station', 'deployed'] : [def.orChoice.zone]);
        zones.forEach((zone) => {
          nextState = grantReward(nextState, buyer.id, {
            kind: 'text', effect: { kind: 'sectorRangeReward', zone, min: def.orChoice.min, max: def.orChoice.max },
          }).state;
        });
      } else if (def.orChoice.kind === 'rollUntilSeven') {
        // Genesis's Dancey ("OR roll 2 dice repeatedly until you roll a
        // 7. Gain 1 vp for each roll.") - a PRIVATE roll sequence (its
        // own rngState draws, not the shared turn dice - same "not
        // affecting other players' allocation" reasoning as bonusRoll),
        // resolved fully here rather than as a pending choice since
        // there's nothing left for the player to decide once they've
        // opted into the gamble. "For each roll" is read as literally
        // every roll made, including the one that finally lands on 7.
        // Bounded defensively at 1000 rolls (7 lands with p=1/6 per
        // roll - this cap is only ever a safety net against a
        // theoretically-infinite loop, not a realistic outcome).
        let { rngState } = nextState.meta;
        let rollCount = 0;
        let sum = null;
        while (sum !== 7 && rollCount < 1000) {
          const [pair, nextRng] = rollTwoDice(rngState);
          rngState = nextRng;
          sum = pair[0] + pair[1];
          rollCount += 1;
        }
        nextState = { ...nextState, meta: { ...nextState.meta, rngState } };
        nextState = applyResourceToPlayer(nextState, buyer.id, { kind: 'vp', amount: rollCount });
      }
    } else if (def.vp != null) {
      nextState = applyResourceToPlayer(nextState, buyer.id, { kind: 'vp', amount: def.vp, source: 'colony' });
    }
    if (def.excessCreditsToVp && excessCredits > 0) {
      nextState = applyResourceToPlayer(nextState, buyer.id, { kind: 'vp', amount: Math.floor(excessCredits / 5), source: 'colony' });
    }
    // Genesis's Reese/Nunn/Yaple/Veger ("Until the end of your next
    // turn, when you would gain X, [double/convert] instead.") - see
    // abilities.js's own applyTemporaryResourceModifiers for where this
    // actually takes effect. turnsRemaining:2 survives the decrement at
    // the end of THIS turn (still active for the buyer's very next one)
    // and is removed by the matching decrement at the end of that next
    // turn - see endTurn's own comment.
    if (def.tempResourceModifier) {
      nextState = {
        ...nextState,
        meta: {
          ...nextState.meta,
          resourceModifiers: {
            ...nextState.meta.resourceModifiers,
            [buyer.id]: [...(nextState.meta.resourceModifiers?.[buyer.id] || []), { ...def.tempResourceModifier, turnsRemaining: 2 }],
          },
        },
      };
    }
    // Genesis's Vlad ("Choose a number. Until the end of your next turn,
    // each player... sets one die to that number.") - global (every
    // player's roll), single slot (only one copy of this colony can ever
    // exist), expiry keyed to the BUYER's own turn count via rollDice/
    // endTurn - see their own comments. `colonyChosenNumber` (1-6,
    // defaulting to 1 if omitted/out of range so a stray param can't
    // silently disable the card the buyer just paid for).
    if (def.forcedDieChoice) {
      const chosen = (colonyChosenNumber >= 1 && colonyChosenNumber <= 6) ? colonyChosenNumber : 1;
      nextState = {
        ...nextState,
        meta: { ...nextState.meta, forcedDieValue: { value: chosen, buyerId: buyer.id, turnsRemaining: 2 } },
      };
    }
    // Genesis's Chu/Burton/Bolme ("You MAY swap all cards - station AND
    // deployed - in your X/Y sectors, or your A/B sectors.") - "may"
    // means a genuine third option of swapping nothing at all, same
    // "don't have to take the alternate" default as orChoice above (no
    // swap happens unless colonyChoice is explicitly 'pairSwap'). Reuses
    // the EXISTING `swapSectors` atomic effect completely unchanged -
    // that effect already swaps the whole `player.sectors[N]` object
    // (`{station, deployedStack}` together), which already IS "station
    // AND deployed" - this was never a station-only swap to begin with.
    if (colonyChoice === 'pairSwap' && def.pairSwapChoice) {
      const pair = def.pairSwapChoice.options[colonyPairIndex === 1 ? 1 : 0];
      if (pair) {
        nextState = applyAtomicEffect(nextState, buyer.id, { kind: 'swapSectors', sectorA: pair[0], sectorB: pair[1] });
      }
    }
    if (def.instantWin) {
      nextState = { ...nextState, meta: { ...nextState.meta, instantWinnerId: buyer.id } };
    }
    // Genesis's "Shared Rewards" colonies (rulebook p4, verified word for
    // word): "The player who bought the colony gains the reward listed
    // above, and all players gain the shared reward listed below." Read
    // literally, "all players" includes the buyer - so the buyer gets
    // BOTH their own top reward (the def.vp grant just above) AND this
    // one, while everyone else only ever gets this one. Uses the same
    // grantReward the rest of the reward system shares (no arrow chain
    // involved - a shared reward is never itself an arrow target).
    if (def.sharedReward) {
      nextState.players.forEach((p) => {
        nextState = grantReward(nextState, p.id, def.sharedReward).state;
      });
    }
    // Genesis's Trudeau colony ("All other players gain 1 income.") -
    // re-verified as its own distinct wording from Shared Rewards' own
    // ("all players", buyer included) - here the buyer explicitly gets
    // NOTHING extra beyond their own top reward, only everyone else does.
    if (def.allOthersReward) {
      nextState.players.forEach((p) => {
        if (p.id === buyer.id) return;
        nextState = grantReward(nextState, p.id, def.allOthersReward).state;
      });
    }
    // Genesis's Paquette colony ("At the end of this turn, all players
    // gain 5 credits.") - combines End of Turn timing (buyCard's own
    // endOfTurnReward comment) with a Shared-Rewards-style breadth
    // (everyone, buyer included) - queued the same way, just tagged
    // `allPlayers` so endTurn() broadcasts it instead of granting only
    // to the original buyer.
    if (def.endOfTurnRewardAllPlayers) {
      nextState = {
        ...nextState,
        meta: {
          ...nextState.meta,
          pendingEndOfTurnRewards: [
            ...(nextState.meta.pendingEndOfTurnRewards || []),
            {
              playerId: buyer.id, reward: def.endOfTurnRewardAllPlayers, allPlayers: true,
            },
          ],
        },
      };
    }
    // Genesis's Hobart/Lashgari ("All players gain [reroll icon].") - the
    // exact same printed reward as the existing `rerollReward` (see this
    // function's own comment above on Greenhaven/Lugger/etc - "a plain
    // reroll icon as a REWARD"), just broadcast to everyone instead of
    // one player. Deliberately NOT modeled as a per-player `sharedReward`
    // loop: `rerollReward` operates on the one shared state.meta.dice,
    // so calling grantReward once per player would grant that many
    // separate re-rolls in a row (wasting RNG draws and only the LAST
    // one actually mattering) instead of the single shared reroll the
    // card's own art clearly depicts.
    if (def.sharedDiceReroll) {
      nextState = rerollDice(nextState, null);
    }
    // Genesis's Laderoute ("All players place 1 charge anywhere.") -
    // reuses the exact same `pendingChargeChoices` primitive already
    // built for Helios/Radiant/Watts's own "place charge on up to N"
    // reward, just queued for EVERY player (not only the buyer) with no
    // sector ceiling (`maxSector: null` - see eligibleChargeTargets's own
    // "anywhere" handling).
    if (def.allPlayersChargeAnywhere) {
      nextState = {
        ...nextState,
        meta: {
          ...nextState.meta,
          pendingChargeChoices: [
            ...(nextState.meta.pendingChargeChoices || []),
            ...nextState.players.map((p) => ({ playerId: p.id, max: 1, maxSector: null })),
          ],
        },
      };
    }
    // Genesis's Wootton (rulebook Card 128's own clarification, matched
    // word for word: "All players place 1 charge on each card possible.")
    // - unlike Laderoute, this is NOT a choice (every eligible card gets
    // one, automatically), so it reuses the existing `placeChargeAll`
    // atomic effect (already built for Nikola's own "place 1 charge on
    // all your OTHER cards") directly for every player, with no
    // `sourceInstanceId` to exclude since no specific card is doing the
    // granting here.
    if (def.allPlayersChargeAllEligible) {
      nextState.players.forEach((p) => {
        nextState = applyAtomicEffect(nextState, p.id, { kind: 'placeChargeAll' }, {});
      });
    }
    // Genesis's Clair ("In turn order, all players place 1 charge, if
    // possible, on any empty charge square of the player to their
    // LEFT.") - the genuinely cross-player sibling of Laderoute: same
    // pendingChargeChoices primitive, but the choosing player and the
    // board being charged are different people (`boardOwnerId`, a new
    // optional field on a charge choice - eligibleChargeTargets/
    // resolveChargeChoice/heuristicBot.js's own AI resolution all read it,
    // defaulting to the chooser's own board when absent so every existing
    // charge choice elsewhere is unaffected). "If possible" is read
    // literally - a player whose left neighbor has nothing chargeable
    // simply gets no choice queued at all, not an empty no-op prompt.
    if (def.allPlayersChargeLeftNeighbor && nextState.players.length > 1) {
      const newChoices = [];
      nextState.players.forEach((p) => {
        const neighborId = leftNeighborId(nextState, p.id);
        if (eligibleChargeTargets(nextState, p.id, null, neighborId).length > 0) {
          newChoices.push({
            playerId: p.id, max: 1, maxSector: null, boardOwnerId: neighborId,
          });
        }
      });
      nextState = {
        ...nextState,
        meta: { ...nextState.meta, pendingChargeChoices: [...(nextState.meta.pendingChargeChoices || []), ...newChoices] },
      };
    }
    // Genesis's Fitzgerald ("In turn order, all players draw a level 2
    // card, and then choose an opponent. That opponent may claim the
    // card or discard it.") - see queueCardGift's own comment for why
    // this queues pendingCardGiftChoices (a "choose WHO decides" step)
    // rather than a normal pendingDeckPeeks entry.
    if (def.allPlayersDrawAndGift) {
      nextState.players.forEach((p) => {
        nextState = queueCardGift(nextState, p.id, def.allPlayersDrawAndGift.level);
      });
    }
    // Genesis's Lepore ("All players draw the top card on the level 1
    // deck and may claim or discard it.") - simpler than Fitzgerald's own
    // draw-then-gift: each player decides for THEMSELVES, so this reuses
    // the existing queueDeckPeek/pendingDeckPeeks/resolveDeckPeek
    // machinery (built for Dalton) directly, once per player, with zero
    // new code.
    if (def.allPlayersDrawAndClaim) {
      nextState.players.forEach((p) => {
        nextState = queueDeckPeek(nextState, p.id, def.allPlayersDrawAndClaim.level, 1);
      });
    }
    // ---- Biodome colony effects (scripts/source-data/biodomeColoniesReady.js) ----
    // Ko-Stovicha ("Set your VP to 39.") / Terasys ("Set your VP equal to
    // 1 less than any other player." - "any" is the buyer's own pick and
    // the leader strictly dominates, so it resolves against the highest
    // OTHER player's VP; same no-real-tradeoff reasoning as Iris's own
    // scaledReward max). A set, not a grant - deliberately NOT routed
    // through applyResourceToPlayer, whose temporary double/convert
    // modifiers apply to gains, not assignments.
    if (def.setVpAbsolute != null) {
      nextState = replacePlayer(nextState, buyer.id, (p) => ({ ...p, vp: def.setVpAbsolute }));
    }
    if (def.setVpRelativeToMax != null && nextState.players.length > 1) {
      const maxOtherVp = Math.max(...nextState.players.filter((p) => p.id !== buyer.id).map((p) => p.vp));
      nextState = replacePlayer(nextState, buyer.id, (p) => ({ ...p, vp: Math.max(0, maxOtherVp + def.setVpRelativeToMax) }));
    }
    // Exanik/Rinova ("Take another turn after this one.") - consumed by
    // endTurn. A queue, not a single slot: two extra-turn colonies bought
    // by different players must not clobber each other.
    if (def.extraTurn) {
      nextState = {
        ...nextState,
        meta: { ...nextState.meta, extraTurnPlayerIds: [...(nextState.meta.extraTurnPlayerIds || []), buyer.id] },
      };
    }
    // Nikovsky ("Take the starting player card and reverse the turn
    // order for the rest of the game.") - turnDirection flips (endTurn's
    // own next-player/final-turn math is direction-aware), and the
    // starting-player card moves to the buyer.
    if (def.reverseTurnOrder) {
      const buyerIndex = nextState.players.findIndex((p) => p.id === buyer.id);
      nextState = {
        ...nextState,
        meta: {
          ...nextState.meta,
          turnDirection: -(nextState.meta.turnDirection || 1),
          startingPlayerIndex: buyerIndex,
        },
      };
    }
    // Hivvu ("Move all deployed cards in sector 1, 2, 3, 4, or 5 into
    // sector 6.") / Cytek ("Move all your deployed cards into sector
    // 9.") - a bulk stack merge on the buyer's own board, preserving
    // stack order (source sectors walked ascending).
    if (def.moveDeployedToSector) {
      const { fromSectors, to } = def.moveDeployedToSector;
      nextState = replacePlayer(nextState, buyer.id, (p) => {
        const sources = (fromSectors === 'all' ? Object.keys(p.sectors).map(Number) : fromSectors)
          .filter((s) => s !== to);
        const moved = [];
        const sectors = { ...p.sectors };
        sources.sort((a, b) => a - b).forEach((s) => {
          moved.push(...sectors[s].deployedStack);
          sectors[s] = { ...sectors[s], deployedStack: [] };
        });
        sectors[to] = { ...sectors[to], deployedStack: [...sectors[to].deployedStack, ...moved] };
        return { ...p, sectors };
      });
    }
    // Grippen ("After the last turn of the game, make 3 additional
    // rolls...") - a global flag endTurn's own game-over path consumes.
    if (def.endgameRitualRolls) {
      nextState = {
        ...nextState,
        meta: { ...nextState.meta, endgameRitualRolls: def.endgameRitualRolls },
      };
    }
    // Grovego ("If a player reaches 40 VP this round, do not end the
    // game. Instead play 1 more round.") - arms a one-shot suppression
    // of the 40-VP trigger; see endTurn.
    if (def.delayGameEndOneRound) {
      nextState = {
        ...nextState,
        meta: { ...nextState.meta, gameEndDelayArmed: true },
      };
    }
    // Coasite ("In turn order, all players claim the top card on the
    // level 2 deck.") - mandatory (no "may"), so it resolves without any
    // pending choice: each player, buyer's turn order, takes the top
    // draw-pile card into its own sector (straight to deployed if a
    // colony already holds that sector, the standard Genesis claim rule).
    if (def.allPlayersClaimTop) {
      const { level: claimLevel } = def.allPlayersClaimTop;
      const startIndex = nextState.players.findIndex((p) => p.id === buyer.id);
      for (let offset = 0; offset < nextState.players.length; offset += 1) {
        const p = nextState.players[(startIndex + offset) % nextState.players.length];
        const draw = nextState.shipyard.draw[claimLevel];
        if (!draw.length) break;
        const [topCardId, ...rest] = draw;
        nextState = {
          ...nextState,
          shipyard: { ...nextState.shipyard, draw: { ...nextState.shipyard.draw, [claimLevel]: rest } },
        };
        const claimedDef = defFor(topCardId);
        nextState = sectorHasColony(nextState.players.find((pl) => pl.id === p.id), claimedDef.sector)
          ? deployCardIntoColonizedSector(nextState, p.id, claimedDef, claimedDef.sector)
          : deployCard(nextState, p.id, claimedDef, claimedDef.sector);
      }
    }
    // Hiveco ("Place 1 charge on any empty charge square.") - Laderoute's
    // own choice shape, buyer only.
    if (def.buyerChargeAnywhere) {
      nextState = {
        ...nextState,
        meta: {
          ...nextState.meta,
          pendingChargeChoices: [
            ...(nextState.meta.pendingChargeChoices || []),
            { playerId: buyer.id, max: def.buyerChargeAnywhere, maxSector: null },
          ],
        },
      };
    }
    // Seedwell ("You may deploy a ship card.") - a generic buy-time
    // grantReward, merged with every pending queue it can produce (the
    // same merge immediateReward's own comment explains). Currently only
    // Seedwell's emptySectorChoice rides this, but the field is generic
    // on purpose.
    if (def.onBuyReward) {
      const granted = grantReward(nextState, buyer.id, def.onBuyReward);
      nextState = {
        ...granted.state,
        meta: {
          ...granted.state.meta,
          pendingClaims: [...(granted.state.meta.pendingClaims || []), ...granted.pendingClaims],
          pendingExtraBuys: [...(granted.state.meta.pendingExtraBuys || []), ...granted.pendingExtraBuys],
          pendingSectorPicks: [...(granted.state.meta.pendingSectorPicks || []), ...granted.pendingSectorPicks],
          pendingChargeChoices: [...(granted.state.meta.pendingChargeChoices || []), ...granted.pendingChargeChoices],
          pendingMoveChoices: [...(granted.state.meta.pendingMoveChoices || []), ...granted.pendingMoveChoices],
          pendingCardSwapChoices: [...(granted.state.meta.pendingCardSwapChoices || []), ...granted.pendingCardSwapChoices],
          pendingEmptySectorChoices: [...(granted.state.meta.pendingEmptySectorChoices || []), ...granted.pendingEmptySectorChoices],
        },
      };
    }
    // Frye-Kova ("All players deploy any 4 of their ship cards.") - four
    // Empty Sectors deploys each, capped at how many occupied sectors a
    // player actually has (same no-empty-prompt reasoning as Clair).
    if (def.allPlayersDeployCount) {
      const newChoices = [];
      nextState.players.forEach((p) => {
        const occupied = Object.values(p.sectors).filter((s) => s.station).length;
        for (let i = 0; i < Math.min(def.allPlayersDeployCount, occupied); i += 1) {
          newChoices.push({ playerId: p.id });
        }
      });
      nextState = {
        ...nextState,
        meta: { ...nextState.meta, pendingEmptySectorChoices: [...(nextState.meta.pendingEmptySectorChoices || []), ...newChoices] },
      };
    }
    // Planton ("Discard a ship card on your board and claim the top card
    // on the level 1 deck.") - the discard is the buyer's pick (a new
    // pendingShipDiscards queue); the claim happens in its resolver.
    if (def.discardShipThenClaim) {
      nextState = {
        ...nextState,
        meta: {
          ...nextState.meta,
          pendingShipDiscards: [
            ...(nextState.meta.pendingShipDiscards || []),
            { playerId: buyer.id, claimLevel: def.discardShipThenClaim.level },
          ],
        },
      };
    }
    // Genesis's Satkowiak ("Choose another player. You and that player
    // each give each other 1 card." - rulebook Card 130). The buyer
    // already chose WHO before dispatching (colonyTargetPlayerId, same
    // pre-dispatch shape as Holcomb); both sides' "which card" picks are
    // queued here, buyer first.
    if (def.tradeCards && colonyTargetPlayerId && colonyTargetPlayerId !== buyer.id
        && nextState.players.some((p) => p.id === colonyTargetPlayerId)) {
      nextState = {
        ...nextState,
        meta: {
          ...nextState.meta,
          pendingCardTradeChoices: [
            ...(nextState.meta.pendingCardTradeChoices || []),
            { playerId: buyer.id, toPlayerId: colonyTargetPlayerId },
            { playerId: colonyTargetPlayerId, toPlayerId: buyer.id },
          ],
        },
      };
    }
    nextState = {
      ...nextState,
      colonies: nextState.colonies.map((c) => (c.cardId === colonyCardId ? { ...c, taken: true, takenByPlayerId: buyer.id } : c)),
    };
  } else {
    // Genesis's Haumea grants a colony-ONLY extra buy ("Buy a COLONY
    // card...") - reject a ship purchase against that same extraBuy
    // index outright, same as any other illegal-move guard here.
    if (extraBuy?.colonyOnly) return state;
    if (!nextState.shipyard.display[level].includes(cardId)) return state;
    const def = defFor(cardId);
    // Biodome's "Nomad Class" ships (and a few Shy Pluto/Genesis cards)
    // print a variable minimum sector ("1+", "8+") instead of a fixed
    // one - the buyer picks any sector at or above that minimum when
    // the card is first acquired (see docs/expansion-audit.md). This
    // applies regardless of whether the purchase is a normal buy or an
    // ability-granted extra buy - either way an explicit, in-range
    // sectorOverride is required.
    let allowedMin = 1;
    let allowedMax = 12;
    if (extraBuy?.sectorRange) [allowedMin, allowedMax] = extraBuy.sectorRange;
    if (def.variableSector) allowedMin = Math.max(allowedMin, def.sector);
    const sectorChoiceRequired = def.variableSector || Boolean(extraBuy?.sectorRange);
    if (sectorChoiceRequired) {
      if (sectorOverride == null || sectorOverride < allowedMin || sectorOverride > allowedMax) return state;
    } else if (sectorOverride != null) {
      // Biodome's Settleraph ("You may place cards you buy in one sector
      // higher than normal.") - the one legal fixed-sector override: the
      // printed sector plus exactly one, while the buyer owns the colony.
      const settleraphOk = ownsOngoingModifier(buyer, 'buyOneSectorHigher')
        && sectorOverride === def.sector + 1 && sectorOverride <= 12;
      if (!settleraphOk) return state;
    }
    // A colonized sector blocks a normal purchase - but a Vosiki
    // deploy-immediately buy is the same shape as the Genesis claim rule
    // ("immediately deployed instead"), so the colony is no obstacle.
    const vosikiDeploy = deployImmediately && ownsOngoingModifier(buyer, 'buyShipsDeployed');
    if (!vosikiDeploy && sectorHasColony(buyer, sectorOverride || def.sector)) return state;
    if (!usingExtraBuy || !extraBuy.free) {
      // Terra Proxima's Stirling (see useAbility's own buyCardDiscount
      // comment) - a discounted, not free, extra buy takes a flat amount
      // off the printed cost, floored at 0.
      const cost = Math.max(0, def.cost - (extraBuy?.discount || 0));
      if (buyer.credits < cost) return state;
      // "keep unspent credits" (see the colony branch's own comment,
      // and abilities.js's keepUnspentCredits case) - a normal buy would
      // otherwise always spend the whole credit cube to 0.
      const keepsUnspent = (nextState.meta.keepsUnspentCreditsPlayerIds || []).includes(buyer.id);
      nextState = replacePlayer(nextState, buyer.id, (p) => ({
        ...p, credits: (usingExtraBuy || keepsUnspent) ? p.credits - cost : 0,
      }));
    }
    // Biodome's Vosiki ("When you buy a ship card, you may immediately
    // deploy it instead of placing the card on your board.") - the
    // bought card goes STRAIGHT onto the deployed stack (deployed side
    // up, nothing bumped, station untouched) when the buyer owns the
    // colony and asked for it.
    if (vosikiDeploy) {
      nextState = deployCardIntoColonizedSector(nextState, buyer.id, def, sectorOverride || def.sector);
    } else {
      nextState = deployCard(nextState, buyer.id, def, sectorOverride);
    }
    nextState = refillDisplaySlot(nextState, level, cardId);
    // Genesis's "Immediate Rewards" (yellow, rulebook p3, verified word
    // for word): "When the card has been bought and placed on your
    // board, its effect resolves immediately and then has no other
    // effect for the rest of the game... cannot be gained using
    // directional arrows and dice-arrow activation abilities, nor
    // doubled by other abilities" - i.e. this NEVER goes through the
    // normal station/deployed dice-roll reward system at all (grantReward
    // directly, no resolveArrowChain involved, same as a colony's own
    // flat def.vp grant just above).
    if (def.immediateReward) {
      // Merges every pending queue grantReward might produce, not just
      // `.state` - Triumph/Ulric's own moveDeployedCard (see grantReward's
      // own comment) is the first immediateReward confirmed to need this,
      // but any future one that claims/buys/picks would have silently
      // lost that pending entry here too before this fix.
      const granted = grantReward(nextState, buyer.id, def.immediateReward);
      nextState = {
        ...granted.state,
        meta: {
          ...granted.state.meta,
          pendingClaims: [...(granted.state.meta.pendingClaims || []), ...granted.pendingClaims],
          pendingExtraBuys: [...(granted.state.meta.pendingExtraBuys || []), ...granted.pendingExtraBuys],
          pendingSectorPicks: [...(granted.state.meta.pendingSectorPicks || []), ...granted.pendingSectorPicks],
          pendingChargeChoices: [...(granted.state.meta.pendingChargeChoices || []), ...granted.pendingChargeChoices],
          pendingMoveChoices: [...(granted.state.meta.pendingMoveChoices || []), ...granted.pendingMoveChoices],
          pendingCardSwapChoices: [...(granted.state.meta.pendingCardSwapChoices || []), ...granted.pendingCardSwapChoices],
          pendingEmptySectorChoices: [...(granted.state.meta.pendingEmptySectorChoices || []), ...granted.pendingEmptySectorChoices],
        },
      };
    }
    // Genesis's "End of Turn" rewards (rulebook p3, verified word for
    // word): "Cards that reward you [credits] at the end of the turn are
    // resolved after setting your [credits] cube to 0, if you bought a
    // card, and after you move your [credits] cube up to be equal with
    // your [income] cube. After its effect resolves, it has no other
    // effect for the rest of the game." That's this engine's own
    // endTurn()'s existing spend-then-income sequence, just clarifying
    // this new reward resolves AFTER it, not instead of or before it -
    // so it's queued here and actually granted in endTurn() below, once,
    // for whichever player's turn is ending when it's found (almost
    // always the SAME turn it was bought in, since buying happens in
    // post-reward phase right before endTurn() - but a passive player's
    // own extra-buy mid-someone-else's-turn naturally defers to THEIR
    // own next turn ending instead, with no special-casing needed).
    if (def.endOfTurnReward) {
      // Weyland/De León (Genesis) print TWO icons in the same End of Turn
      // box (e.g. income + vp) - same "combined values" convention as a
      // normal per-hit reward's own secondaryReward (resolveArrowChain's
      // own comment), just queued as a second entry here since
      // pendingEndOfTurnRewards/grantReward only ever carry one plain
      // reward each.
      const entries = [{ playerId: buyer.id, reward: def.endOfTurnReward }];
      if (def.endOfTurnSecondaryReward) entries.push({ playerId: buyer.id, reward: def.endOfTurnSecondaryReward });
      nextState = {
        ...nextState,
        meta: {
          ...nextState.meta,
          pendingEndOfTurnRewards: [...(nextState.meta.pendingEndOfTurnRewards || []), ...entries],
        },
      };
    }
  }

  if (usingExtraBuy) {
    nextState = {
      ...nextState,
      meta: { ...nextState.meta, pendingExtraBuys: nextState.meta.pendingExtraBuys.filter((_, i) => i !== extraBuyIndex) },
    };
  } else {
    nextState = { ...nextState, meta: { ...nextState.meta, turnPhase: 'post-buy' } };
  }
  return nextState;
}

export function skipBuy(state) {
  if (state.meta.turnPhase !== 'post-reward') return state;
  return { ...state, meta: { ...state.meta, turnPhase: 'post-buy' } };
}

// Resolves a pending "choose the sector to your left or right" pick
// (see resolveArrowChain's own comment) - chosenSector must be one of
// the two options actually offered. Applies whatever that sector's own
// (fully chain-resolved) reward grants (a plain resource, or a claimCard/
// buyCard text effect, via the same grantReward helper applyHit's own
// normal path uses) and removes the pending entry. Whatever was already
// resolved BEFORE reaching this choice point was already granted the
// moment the choice was queued (settleArrowChain's own comment) - only
// the still-ambiguous branch waits on this.
//
// The picked option is itself a resolved sub-chain (see resolveArrowChain)
// - most of the time that just means granting its own ownBaseRewards/
// leafReward straight away, but if the picked branch ran into a SECOND
// both-direction arrow of its own, its own choiceGroups is non-empty
// instead, and settleArrowChain below stashes a fresh pending choice for
// it rather than granting anything yet - chaining a choice into another
// choice, the same way a single-direction arrow chains straight through
// one.
export function resolveArrowChoice(state, { playerId, choiceIndex, chosenSector }) {
  const choice = state.meta.pendingArrowChoices?.[choiceIndex];
  if (!choice || choice.playerId !== playerId) return state;
  const option = choice.options.find((o) => o.sector === chosenSector);
  if (!option) return state;
  const settled = settleArrowChain(
    state,
    playerId,
    { ownBaseRewards: option.ownBaseRewards, leafReward: option.leafReward, choiceGroups: option.choiceGroups },
    choice.sourceSector,
    choice.instanceId,
  );
  const nextState = settled.state;
  const remainingChoices = nextState.meta.pendingArrowChoices.filter((_, i) => i !== choiceIndex);
  return {
    ...nextState,
    meta: {
      ...nextState.meta,
      pendingArrowChoices: [...remainingChoices, ...settled.pendingArrowChoices],
      pendingClaims: [...(nextState.meta.pendingClaims || []), ...settled.pendingClaims],
      pendingExtraBuys: [...(nextState.meta.pendingExtraBuys || []), ...settled.pendingExtraBuys],
      pendingSectorPicks: [...(nextState.meta.pendingSectorPicks || []), ...settled.pendingSectorPicks],
      pendingChargeChoices: [...(nextState.meta.pendingChargeChoices || []), ...settled.pendingChargeChoices],
      pendingMoveChoices: [...(nextState.meta.pendingMoveChoices || []), ...settled.pendingMoveChoices],
      pendingCardSwapChoices: [...(nextState.meta.pendingCardSwapChoices || []), ...settled.pendingCardSwapChoices],
      pendingEmptySectorChoices: [...(nextState.meta.pendingEmptySectorChoices || []), ...settled.pendingEmptySectorChoices],
    },
  };
}

// Total charges currently sitting on a player's own cards - station
// squares AND deployed squares, every sector - optionally restricted to
// a specific set of sectors (Guild of Merchants/Cosmo's own "in this and
// adjacent sectors" - see resolveScaledReward's own comment).
function countPlayerCharges(state, playerId, sectorFilter) {
  const player = state.players.find((p) => p.id === playerId);
  let total = 0;
  Object.entries(player.sectors).forEach(([sectorStr, sector]) => {
    if (sectorFilter && !sectorFilter(Number(sectorStr))) return;
    if (sector.station) total += sector.station.stationCharges;
    sector.deployedStack.forEach((d) => { total += d.deployedCharges; });
  });
  return total;
}

// Number of the player's own sectors whose deployed stack has at least
// `threshold` cards (Federation of Commerce/United Treasury/Valentine/
// Zephyr's own "X per sector with at least N deployed cards").
function countDenseSectors(state, playerId, threshold) {
  const player = state.players.find((p) => p.id === playerId);
  return Object.values(player.sectors).filter((s) => s.deployedStack.length >= threshold).length;
}

// Number of colonies this player currently owns (JOI's own "gain 2
// credits for each colony card you have").
function countPlayerColonies(state, playerId) {
  return state.colonies.filter((c) => c.takenByPlayerId === playerId).length;
}

// Number of the player's own cards (station or deployed, any sector)
// whose own def has a printed Immediate Reward (Iris's own "yellow
// reward" count - rulebook p3's own "yellow background" = Immediate
// Reward category). Counts the CARD, not whether its own immediateReward
// has already fired - that's a one-time buy-time trigger unrelated to
// whether the card still counts as "a yellow reward on your board" here.
//
// Matt caught this directly, live: an End of Turn reward (Rico/Polaris/
// Columbo/Gallagher/Bear - endOfTurnReward, buyCard's own comment)
// prints in the SAME yellow box as a plain Immediate Reward, and is the
// same underlying category - "triggering when you buy them but with a
// timer on them when they actually take effect," his own words - not a
// mechanically separate thing this count should exclude. Missing this
// is exactly why Iris granted only 2 vp (flatAmount) instead of the
// real, much higher scaled count on a board with several End of Turn
// cards - Math.max(2, scaledAmount) was correctly taking the better of
// the two, it just had the wrong scaledAmount to compare against.
function countPlayerImmediateRewardCards(state, playerId) {
  const player = state.players.find((p) => p.id === playerId);
  const isYellowReward = (cardId) => {
    const def = defFor(cardId);
    return Boolean(def.immediateReward || def.endOfTurnReward);
  };
  let total = 0;
  Object.values(player.sectors).forEach((sector) => {
    if (sector.station && isYellowReward(sector.station.cardId)) total += 1;
    sector.deployedStack.forEach((d) => { if (isYellowReward(d.cardId)) total += 1; });
  });
  return total;
}

// Genesis's "board-state-scaled reward" cards - several confirmed with
// this exact shape, re-verified directly against the card art (plain
// blue/red reward boxes, no charge squares - a per-hit reward like any
// other, not a chargeable ability): Guild of Merchants/Federation of
// Commerce/United Treasury/Cosmo/Mercury/Valentine/Zephyr/JOI. Each
// grants `effect.reward.amount` multiplied by a count of some board-state
// quantity - `effect.per` selects which:
//   - 'charge': every charge on every one of the player's own cards.
//   - 'chargeAdjacent': same, but only sectors within 1 of `sourceSector`
//     (the card's own printed sector - "in this and adjacent sectors").
//   - 'denseSector': sectors with at least `effect.threshold` deployed
//     cards.
//   - 'colony': colonies this player currently owns.
//   - 'immediateRewardCard': cards on this player's own board (station or
//     deployed) with a printed Immediate Reward (Iris's own "yellow
//     reward" count).
// `effect.flatAmount`, if present (Iris only, so far - a genuine printed
// "gain X OR gain Y per Z" choice with no other tradeoff riding on which
// branch wins), takes the larger of the flat and scaled amounts instead
// of always scaling.
// Resolves immediately like any other reward (no pending choice) - the
// count is just read off live board state at grant time. `sourceSector`
// is only ever non-null when this reward is being granted as a direct
// hit on its own card (settleArrowChain's own comment on why chained-
// through hops don't currently carry a per-hop sector) - every
// transcribed card using 'chargeAdjacent' is a plain, non-arrow reward,
// so this isn't a real limitation for any known card today.
function resolveScaledReward(state, playerId, effect, sourceSector) {
  let count;
  if (effect.per === 'charge') {
    count = countPlayerCharges(state, playerId, null);
  } else if (effect.per === 'chargeAdjacent') {
    count = countPlayerCharges(state, playerId, (s) => sourceSector != null && Math.abs(s - sourceSector) <= 1);
  } else if (effect.per === 'denseSector') {
    count = countDenseSectors(state, playerId, effect.threshold);
  } else if (effect.per === 'colony') {
    count = countPlayerColonies(state, playerId);
  } else if (effect.per === 'immediateRewardCard') {
    count = countPlayerImmediateRewardCards(state, playerId);
  } else {
    count = 0;
  }
  const scaledAmount = effect.reward.amount * count;
  // Genesis's Iris ("Gain 2 vp or gain 1 vp for each yellow (immediate)
  // reward on your board.") - a real printed OR, but purely a magnitude
  // comparison with no other tradeoff (same reward kind either way, nothing
  // else riding on which branch is "chosen") - so taking whichever amount
  // is larger is a faithful, deterministic resolution of the choice
  // itself, not a simplification of it. `flatAmount` is optional - every
  // other scaledReward card (Guild of Merchants/Federation of Commerce/
  // etc.) omits it and just always scales, unaffected.
  const amount = effect.flatAmount != null ? Math.max(effect.flatAmount, scaledAmount) : scaledAmount;
  const scaled = { kind: effect.reward.kind, amount };
  return applyResourceToPlayer(state, playerId, scaled);
}

// The player seated immediately after this one in turn order ("to your
// left" - `state.players` is already ordered by seat/turn-order, per
// endTurn's own `(finishedPlayerIndex + 1) % length` advance).
function leftNeighborId(state, playerId) {
  const index = state.players.findIndex((p) => p.id === playerId);
  return state.players[(index + 1) % state.players.length].id;
}

function rightNeighborId(state, playerId) {
  const index = state.players.findIndex((p) => p.id === playerId);
  return state.players[(index - 1 + state.players.length) % state.players.length].id;
}

// Genesis's The Flying Dutchman ("If possible, gain the blue rewards for
// the card in this sector on the board of the player to your left or
// right.") - reads a DIFFERENT player's own station slot at the same
// sector number this card itself occupies, chains through it exactly
// like a normal same-player hit would (still entirely on the NEIGHBOR's
// own board, including any further arrow their card has), but grants the
// result to the ACTIVATING player. Shared by both the 2-player
// auto-resolve path below (no real choice - left and right are the same
// single opponent) and resolveNeighborChoice's own real pick (3+
// players).
function resolveNeighborStationRewardAgainst(state, playerId, sourceSector, neighborId) {
  const chain = resolveArrowChain(
    ownSectorSlotDefs(state, neighborId, sourceSector, 'station'),
    sourceSector,
    'station',
    (targetSector, zone) => ownSectorSlotDefs(state, neighborId, targetSector, zone),
    new Set([visitKey(sourceSector, 'station')]),
  );
  // If the neighbor's OWN card at this sector is ALSO a Flying-Dutchman-
  // style cross-player read, chaining into it would hop to a THIRD
  // player (or cycle back through the ring of players entirely) with no
  // cross-call cycle detection - resolveArrowChain's own `visited` guard
  // only ever covers ONE player's board, not a chain across several. A
  // real cycle (e.g. two copies of this ship pointing at each other)
  // would recurse unboundedly. Treated as "not possible" (the rulebook's
  // own qualifier) rather than chased further - matches this card's own
  // printed condition instead of crashing or hanging. This also means a
  // pendingNeighborChoices entry can never nest another one, unlike
  // pendingBonusRolls - always safe to resolve/remove by a plain index.
  if (chain.leafReward?.effect?.kind === 'neighborStationReward') chain.leafReward = null;
  const settled = settleArrowChain(state, playerId, chain, sourceSector, null);
  return {
    ...settled.state,
    meta: {
      ...settled.state.meta,
      pendingClaims: [...(settled.state.meta.pendingClaims || []), ...settled.pendingClaims],
      pendingExtraBuys: [...(settled.state.meta.pendingExtraBuys || []), ...settled.pendingExtraBuys],
      pendingSectorPicks: [...(settled.state.meta.pendingSectorPicks || []), ...settled.pendingSectorPicks],
      pendingChargeChoices: [...(settled.state.meta.pendingChargeChoices || []), ...settled.pendingChargeChoices],
      pendingMoveChoices: [...(settled.state.meta.pendingMoveChoices || []), ...settled.pendingMoveChoices],
      pendingCardSwapChoices: [...(settled.state.meta.pendingCardSwapChoices || []), ...settled.pendingCardSwapChoices],
      pendingEmptySectorChoices: [...(settled.state.meta.pendingEmptySectorChoices || []), ...settled.pendingEmptySectorChoices],
      pendingArrowChoices: [...(settled.state.meta.pendingArrowChoices || []), ...settled.pendingArrowChoices],
    },
  };
}

// Matt reported this directly: "go through and do a full audit... The
// goal is to complete all the features in the game... when I report a
// bug, it is REAL and I want it fixed." The rulebook prints this as a
// genuine "left OR right" choice - previously always resolved against the
// left neighbor (a documented simplification, docs/expansion-decisions-
// needed.md). In a 2-player game left and right ARE the same single
// opponent, so there's nothing to actually ask - resolves immediately,
// same "don't bother asking when it's not a real choice" convention
// bonus rolls/allocation already follow. With 3+ players, queues a real
// pendingNeighborChoices entry instead, resolved by resolveNeighborChoice
// below once GameBoard.vue's own UI (or the AI) picks a real neighbor.
function resolveNeighborStationReward(state, playerId, sourceSector) {
  if (sourceSector == null || state.players.length < 2) return state;
  const leftId = leftNeighborId(state, playerId);
  const rightId = rightNeighborId(state, playerId);
  if (leftId === rightId) return resolveNeighborStationRewardAgainst(state, playerId, sourceSector, leftId);
  return {
    ...state,
    meta: {
      ...state.meta,
      pendingNeighborChoices: [...(state.meta.pendingNeighborChoices || []), {
        playerId, sourceSector, leftId, rightId,
      }],
    },
  };
}

// Resolves a pendingNeighborChoices entry once a human (GameBoard.vue's
// own UI, reusing the same "tap a highlighted player tab" idiom
// Fitzgerald's own pendingCardGiftChoices already established) or the AI
// (resolveNeighborChoiceForAI, heuristicBot.js) picks a real neighbor.
// `chosenPlayerId` must be one of the two legal neighbors this exact
// entry was queued with - a stray/forged id is a safe no-op, same
// defensive shape as resolveArrowChoice's own option-membership check.
export function resolveNeighborChoice(state, { playerId, choiceIndex, chosenPlayerId }) {
  const choice = state.meta.pendingNeighborChoices?.[choiceIndex];
  if (!choice || choice.playerId !== playerId) return state;
  if (chosenPlayerId !== choice.leftId && chosenPlayerId !== choice.rightId) return state;
  const resolved = resolveNeighborStationRewardAgainst(state, playerId, choice.sourceSector, chosenPlayerId);
  return {
    ...resolved,
    meta: {
      ...resolved.meta,
      pendingNeighborChoices: resolved.meta.pendingNeighborChoices.filter((_, i) => i !== choiceIndex),
    },
  };
}

// Genesis's "place 1 charge on all your cards with an open charge
// square" (Nikola's own chargeable ability, and Watts's own one-time
// reward variant of the identical text - see grantReward's own
// chargeAllReward comment) - every one of the player's own cards
// (station AND every deployed stack entry, every sector) with an
// ability and room left gets exactly 1 charge. Unlike Helios/Radiant's
// "up to 3 different cards" (a real player choice, still needing new UI
// - see docs/expansion-decisions-needed.md), "all" has nothing to choose
// between, so this resolves immediately with no pending queue.
function chargeAllEligibleCards(state, playerId) {
  let nextState = state;
  const player = nextState.players.find((p) => p.id === playerId);
  Object.keys(player.sectors).forEach((sectorStr) => {
    const sectorNum = Number(sectorStr);
    const currentPlayer = nextState.players.find((p) => p.id === playerId);
    const sector = currentPlayer.sectors[sectorNum];
    if (sector.station) {
      const stationAbility = defFor(sector.station.cardId).station?.ability;
      if (stationAbility && sector.station.stationCharges < stationAbility.squares) {
        nextState = replacePlayer(nextState, playerId, (p) => ({
          ...p,
          sectors: {
            ...p.sectors,
            [sectorNum]: { ...p.sectors[sectorNum], station: addCharge(p.sectors[sectorNum].station, 'station', stationAbility.squares) },
          },
        }));
      }
    }
    sector.deployedStack.forEach((instance) => {
      const deployedAbility = defFor(instance.cardId).deployed?.ability;
      if (!deployedAbility || instance.deployedCharges >= deployedAbility.squares) return;
      nextState = replacePlayer(nextState, playerId, (p) => ({
        ...p,
        sectors: {
          ...p.sectors,
          [sectorNum]: {
            ...p.sectors[sectorNum],
            deployedStack: p.sectors[sectorNum].deployedStack.map((d) => (
              d.instanceId === instance.instanceId ? addCharge(d, 'deployed', deployedAbility.squares) : d
            )),
          },
        },
      }));
    });
  });
  return nextState;
}

// Genesis colonies with "OR gain your blue/red rewards for sectors X-Y"
// (several confirmed cards, e.g. Tzioti/Wood/Buckendorf/Manolis/Eaton/
// McCool - fixed ranges like "1-6" or "7, 8 and 9") and Terra Proxima's
// own Nemesis ship ("Gain Blue rewards for all of your sectors 1-11") -
// EVERY sector in a fixed range is granted, never a player choice (that's
// the difference from Shy Pluto's pickSectors above - no ambiguity to
// resolve, so this never needs a pending queue, just resolves right away
// like a plain reward). `zone` can be 'station', 'deployed', or 'both'
// (Zimmerman's own "gain all your red AND blue rewards"). Self-contained
// like enqueueBonusRoll rather than routed through grantReward's own
// narrower return shape, since touching several sectors' own reward
// chains can surface pendingArrowChoices too (a sector in range whose own
// card has a plain left-or-right arrow), not just claims/extra-buys/
// sector-picks.
function resolveSectorRangeReward(state, playerId, effect) {
  const zones = effect.zone === 'both' ? ['station', 'deployed'] : [effect.zone || 'station'];
  let nextState = state;
  const pendingClaims = [];
  const pendingExtraBuys = [];
  const pendingArrowChoices = [];
  const pendingSectorPicks = [];
  const pendingChargeChoices = [];
  const pendingMoveChoices = [];
  const pendingCardSwapChoices = [];
  const pendingEmptySectorChoices = [];
  for (let sectorNum = effect.min; sectorNum <= effect.max; sectorNum += 1) {
    zones.forEach((zone) => {
      const chain = resolveArrowChain(
        ownSectorSlotDefs(nextState, playerId, sectorNum, zone),
        sectorNum,
        zone,
        (targetSector, z) => ownSectorSlotDefs(nextState, playerId, targetSector, z),
        new Set([visitKey(sectorNum, zone)]),
      );
      const settled = settleArrowChain(nextState, playerId, chain, sectorNum, null);
      nextState = settled.state;
      pendingClaims.push(...settled.pendingClaims);
      pendingExtraBuys.push(...settled.pendingExtraBuys);
      pendingArrowChoices.push(...settled.pendingArrowChoices);
      pendingSectorPicks.push(...settled.pendingSectorPicks);
      pendingChargeChoices.push(...settled.pendingChargeChoices);
      pendingMoveChoices.push(...settled.pendingMoveChoices);
      pendingCardSwapChoices.push(...settled.pendingCardSwapChoices);
      pendingEmptySectorChoices.push(...settled.pendingEmptySectorChoices);
    });
  }
  return {
    ...nextState,
    meta: {
      ...nextState.meta,
      pendingClaims: [...(nextState.meta.pendingClaims || []), ...pendingClaims],
      pendingExtraBuys: [...(nextState.meta.pendingExtraBuys || []), ...pendingExtraBuys],
      pendingArrowChoices: [...(nextState.meta.pendingArrowChoices || []), ...pendingArrowChoices],
      pendingSectorPicks: [...(nextState.meta.pendingSectorPicks || []), ...pendingSectorPicks],
      pendingChargeChoices: [...(nextState.meta.pendingChargeChoices || []), ...pendingChargeChoices],
      pendingMoveChoices: [...(nextState.meta.pendingMoveChoices || []), ...pendingMoveChoices],
      pendingCardSwapChoices: [...(nextState.meta.pendingCardSwapChoices || []), ...pendingCardSwapChoices],
      pendingEmptySectorChoices: [...(nextState.meta.pendingEmptySectorChoices || []), ...pendingEmptySectorChoices],
    },
  };
}

// Resolves Shy Pluto's "gain rewards for any N sectors 1-6" pick
// (rulebook p9, verified word for word: "you gain the blue rewards for a
// number of unique cards on your board in sectors 1-6. You may not gain
// the same blue reward more than once.") - `chosenSectors` must be
// exactly `pick.count` DISTINCT sector numbers within [pick.min,
// pick.max] (distinct sectors are automatically distinct cards, so the
// rulebook's "not more than once" constraint falls out for free). Each
// chosen sector's own reward in `pick.zone` is granted through the exact
// same chain-following machinery as a normal hit (ownSectorSlotDefs/
// resolveArrowChain/settleArrowChain) - the rulebook doesn't address
// whether a picked sector's own further arrow should keep chaining, but
// "gain the reward for a sector" means the same thing everywhere else in
// this engine, so this stays consistent rather than special-casing a
// plain, non-chaining grant just for this one reward type.
export function resolveSectorPick(state, { playerId, pickIndex, chosenSectors }) {
  const pick = state.meta.pendingSectorPicks?.[pickIndex];
  if (!pick || pick.playerId !== playerId) return state;
  if (new Set(chosenSectors).size !== pick.count) return state;
  if (chosenSectors.some((s) => s < pick.min || s > pick.max)) return state;
  if ((pick.excludeSectors || []).some((s) => chosenSectors.includes(s))) return state;

  let nextState = state;
  const pendingClaims = [];
  const pendingExtraBuys = [];
  const pendingArrowChoices = [];
  const pendingSectorPicks = [];
  const pendingChargeChoices = [];
  const pendingMoveChoices = [];
  const pendingCardSwapChoices = [];
  const pendingEmptySectorChoices = [];
  // Carried forward into any new pickSectors pick this resolution
  // produces (see grantReward's own pickSectors case) - every sector
  // already spent ANYWHERE in this causal chain, not just this one hop,
  // so a longer cycle across several relocated pickSectors cards closes
  // exactly like the direct self-reference case does.
  const carriedExcludes = [...new Set([...(pick.excludeSectors || []), ...chosenSectors])];
  chosenSectors.forEach((sectorNum) => {
    const chain = resolveArrowChain(
      ownSectorSlotDefs(nextState, playerId, sectorNum, pick.zone),
      sectorNum,
      pick.zone,
      (targetSector, zone) => ownSectorSlotDefs(nextState, playerId, targetSector, zone),
      new Set([visitKey(sectorNum, pick.zone)]),
    );
    const settled = settleArrowChain(nextState, playerId, chain, sectorNum, null);
    nextState = settled.state;
    pendingClaims.push(...settled.pendingClaims);
    pendingExtraBuys.push(...settled.pendingExtraBuys);
    pendingArrowChoices.push(...settled.pendingArrowChoices);
    pendingChargeChoices.push(...settled.pendingChargeChoices);
    pendingMoveChoices.push(...settled.pendingMoveChoices);
    pendingCardSwapChoices.push(...settled.pendingCardSwapChoices);
    pendingEmptySectorChoices.push(...settled.pendingEmptySectorChoices);
    pendingSectorPicks.push(...settled.pendingSectorPicks.map((newPick) => ({
      ...newPick,
      excludeSectors: [...new Set([...carriedExcludes, ...(newPick.excludeSectors || [])])],
    })));
  });

  const remainingPicks = nextState.meta.pendingSectorPicks.filter((_, i) => i !== pickIndex);
  return {
    ...nextState,
    meta: {
      ...nextState.meta,
      pendingSectorPicks: [...remainingPicks, ...pendingSectorPicks],
      pendingClaims: [...(nextState.meta.pendingClaims || []), ...pendingClaims],
      pendingExtraBuys: [...(nextState.meta.pendingExtraBuys || []), ...pendingExtraBuys],
      pendingArrowChoices: [...(nextState.meta.pendingArrowChoices || []), ...pendingArrowChoices],
      pendingChargeChoices: [...(nextState.meta.pendingChargeChoices || []), ...pendingChargeChoices],
      pendingMoveChoices: [...(nextState.meta.pendingMoveChoices || []), ...pendingMoveChoices],
      pendingCardSwapChoices: [...(nextState.meta.pendingCardSwapChoices || []), ...pendingCardSwapChoices],
      pendingEmptySectorChoices: [...(nextState.meta.pendingEmptySectorChoices || []), ...pendingEmptySectorChoices],
    },
  };
}

// Every one of this player's own cards (station or deployed, any
// stacked deployed instance included) that has an ability with room for
// another charge, within `maxSector` (null means no limit) - shared by
// resolveChargeChoice's own validation and heuristicBot.js's own AI
// resolution, so both always agree on what's actually legal.
// `boardOwnerId` (defaults to `playerId`) is the board actually being
// read - distinct from `playerId` only for Genesis's Clair ("all players
// place 1 charge... on the player to their LEFT"), where the player
// making the choice and the board it lands on are two different people.
export function eligibleChargeTargets(state, playerId, maxSector, boardOwnerId = playerId) {
  const player = state.players.find((p) => p.id === boardOwnerId);
  const targets = [];
  Object.keys(player.sectors).forEach((sectorStr) => {
    const sectorNum = Number(sectorStr);
    if (maxSector != null && sectorNum > maxSector) return;
    const sector = player.sectors[sectorNum];
    if (sector.station) {
      const ability = defFor(sector.station.cardId).station?.ability;
      if (ability && sector.station.stationCharges < ability.squares) {
        targets.push({ sectorNum, slotKey: 'station', instanceId: sector.station.instanceId });
      }
    }
    sector.deployedStack.forEach((instance) => {
      const ability = defFor(instance.cardId).deployed?.ability;
      if (ability && instance.deployedCharges < ability.squares) {
        targets.push({ sectorNum, slotKey: 'deployed', instanceId: instance.instanceId });
      }
    });
  });
  return targets;
}

// Genesis's Helios/Radiant/Watts (deployed) - "Place 1 charge on up to 3
// different cards in this sector or lower." (grantReward's own
// placeChargeUpToN comment). `targets` may be anywhere from 0 up to
// `choice.max` entries - a genuine "up to N", unlike pickSectors' own
// exact count, so declining entirely is just an empty array, no separate
// decline action needed.
export function resolveChargeChoice(state, { playerId, choiceIndex, targets }) {
  const choice = state.meta.pendingChargeChoices?.[choiceIndex];
  if (!choice || choice.playerId !== playerId) return state;
  if (targets.length > choice.max) return state;
  if (new Set(targets.map((t) => t.instanceId)).size !== targets.length) return state;
  const boardOwnerId = choice.boardOwnerId || playerId;
  const eligibleIds = new Set(eligibleChargeTargets(state, playerId, choice.maxSector, boardOwnerId).map((t) => t.instanceId));
  if (targets.some((t) => !eligibleIds.has(t.instanceId))) return state;

  let nextState = state;
  targets.forEach(({ sectorNum, slotKey, instanceId }) => {
    nextState = replacePlayer(nextState, boardOwnerId, (p) => {
      const sector = p.sectors[sectorNum];
      if (slotKey === 'station') {
        const ability = defFor(sector.station.cardId).station.ability;
        return {
          ...p,
          sectors: { ...p.sectors, [sectorNum]: { ...sector, station: addCharge(sector.station, 'station', ability.squares) } },
        };
      }
      return {
        ...p,
        sectors: {
          ...p.sectors,
          [sectorNum]: {
            ...sector,
            deployedStack: sector.deployedStack.map((d) => {
              if (d.instanceId !== instanceId) return d;
              const ability = defFor(d.cardId).deployed.ability;
              return addCharge(d, 'deployed', ability.squares);
            }),
          },
        },
      };
    });
  });
  return {
    ...nextState,
    meta: {
      ...nextState.meta,
      pendingChargeChoices: state.meta.pendingChargeChoices.filter((_, i) => i !== choiceIndex),
    },
  };
}

// Genesis's Triumph/Ulric (Cards 92/70) - "Move 1 deployed card into
// sector X" (grantReward's own moveDeployedCard comment). `instanceId`
// must currently be somewhere in this player's own deployedStack (any
// sector), `destinationSector` must be one of the pick's own printed
// destinations. The card carries its own accumulated charges with it
// unchanged (Card 92/70's own clarification: "the card must remain
// deployed after it moves" - nothing suggests charges reset) - this is
// a relocation, not a re-deploy, so no addCharge/bump logic applies.
export function resolveMoveChoice(state, {
  playerId, choiceIndex, instanceId, destinationSector,
}) {
  const choice = state.meta.pendingMoveChoices?.[choiceIndex];
  if (!choice || choice.playerId !== playerId) return state;
  const player = state.players.find((p) => p.id === playerId);
  const sourceSectorEntry = Object.entries(player.sectors)
    .find(([, s]) => s.deployedStack.some((d) => d.instanceId === instanceId));
  if (!sourceSectorEntry) return state;
  const [sourceSectorStr, sourceSector] = sourceSectorEntry;
  const sourceSectorNum = Number(sourceSectorStr);
  // Ulric's own "left or right" is relative to whichever card gets
  // chosen, computed here rather than checked against a fixed printed
  // list (see grantReward's own moveDeployedCard comment).
  const allowedDestinations = choice.adjacentOnly
    ? [sourceSectorNum - 1, sourceSectorNum + 1].filter((s) => s >= 1 && s <= 12)
    : choice.destinations;
  if (!allowedDestinations.includes(destinationSector)) return state;
  if (sourceSectorNum === destinationSector) return state;
  const instance = sourceSector.deployedStack.find((d) => d.instanceId === instanceId);

  const nextState = replacePlayer(state, playerId, (p) => ({
    ...p,
    sectors: {
      ...p.sectors,
      [sourceSectorNum]: {
        ...p.sectors[sourceSectorNum],
        deployedStack: p.sectors[sourceSectorNum].deployedStack.filter((d) => d.instanceId !== instanceId),
      },
      [destinationSector]: {
        ...p.sectors[destinationSector],
        deployedStack: [...p.sectors[destinationSector].deployedStack, instance],
      },
    },
  }));
  return {
    ...nextState,
    meta: {
      ...nextState.meta,
      pendingMoveChoices: state.meta.pendingMoveChoices.filter((_, i) => i !== choiceIndex),
    },
  };
}

// Genesis's Maddock (Card 69) - "Exchange any 2 ship cards in adjacent
// sectors with each other" (grantReward's own exchangeAdjacentCard
// comment): pick ONE card from each of two ADJACENT sectors, both the
// same zone (`zone: 'station'` swaps whatever sits at each sector's
// station; `zone: 'deployed'` swaps one specific instance out of each
// sector's own deployedStack, identified by instanceIdA/instanceIdB) -
// never one station for one deployed card. Charges/deployed-position
// within the destination stack are otherwise untouched, same
// relocation-not-redeploy behavior as resolveMoveChoice above.
export function resolveCardSwapChoice(state, {
  playerId, choiceIndex, sectorA, sectorB, zone, instanceIdA, instanceIdB,
}) {
  const choice = state.meta.pendingCardSwapChoices?.[choiceIndex];
  if (!choice || choice.playerId !== playerId) return state;
  if (Math.abs(sectorA - sectorB) !== 1) return state;
  // Valiant/Leslie's own anchored variant (grantReward's own
  // exchangeWithAdjacentSector comment) - one side of the swap MUST be
  // this card's own sector, unlike Maddock's unrestricted pick of any
  // adjacent pair.
  if (choice.anchorSector != null && sectorA !== choice.anchorSector && sectorB !== choice.anchorSector) return state;
  const player = state.players.find((p) => p.id === playerId);
  const secA = player.sectors[sectorA];
  const secB = player.sectors[sectorB];
  if (!secA || !secB) return state;

  let nextPlayer;
  if (zone === 'station') {
    nextPlayer = {
      ...player,
      sectors: {
        ...player.sectors,
        [sectorA]: { ...secA, station: secB.station },
        [sectorB]: { ...secB, station: secA.station },
      },
    };
  } else if (zone === 'deployed') {
    const instA = secA.deployedStack.find((d) => d.instanceId === instanceIdA);
    const instB = secB.deployedStack.find((d) => d.instanceId === instanceIdB);
    if (!instA || !instB) return state;
    nextPlayer = {
      ...player,
      sectors: {
        ...player.sectors,
        [sectorA]: { ...secA, deployedStack: [...secA.deployedStack.filter((d) => d.instanceId !== instanceIdA), instB] },
        [sectorB]: { ...secB, deployedStack: [...secB.deployedStack.filter((d) => d.instanceId !== instanceIdB), instA] },
      },
    };
  } else {
    return state;
  }

  const nextState = replacePlayer(state, playerId, () => nextPlayer);
  return {
    ...nextState,
    meta: {
      ...nextState.meta,
      pendingCardSwapChoices: state.meta.pendingCardSwapChoices.filter((_, i) => i !== choiceIndex),
    },
  };
}

// Genesis's Pharos (deployed) - "Deploy any 1 ship card" (grantReward's
// own emptySectorChoice comment): the player picks any ONE of their own
// currently-occupied sectors to empty (rulebook p5's own "Empty Sectors"
// rule - see emptySector's own comment above).
// Seedwell's "may" (and the harmless general case): an empty-sector
// deploy choice can be waved off. Frye-Kova's own entries are strictly
// mandatory by the printed text, but declining a deploy only ever hurts
// the decliner, so one decline path serves every source of this choice.
export function declineEmptySectorChoice(state, { playerId, choiceIndex }) {
  const choice = state.meta.pendingEmptySectorChoices?.[choiceIndex];
  if (!choice || choice.playerId !== playerId) return state;
  return {
    ...state,
    meta: {
      ...state.meta,
      pendingEmptySectorChoices: state.meta.pendingEmptySectorChoices.filter((_, i) => i !== choiceIndex),
    },
  };
}

// Where a card instance lives on a player's board, colonies included -
// the callers below decide what's a legal pick.
function findBoardInstance(player, instanceId) {
  for (const [sectorNum, s] of Object.entries(player.sectors)) {
    if (s.station?.instanceId === instanceId) return { sector: Number(sectorNum), where: 'station', instance: s.station };
    const deployed = s.deployedStack.find((d) => d.instanceId === instanceId);
    if (deployed) return { sector: Number(sectorNum), where: 'deployed', instance: deployed };
  }
  return null;
}

function removeBoardInstance(state, playerId, found) {
  return replacePlayer(state, playerId, (p) => ({
    ...p,
    sectors: {
      ...p.sectors,
      [found.sector]: found.where === 'station'
        ? { ...p.sectors[found.sector], station: null }
        : { ...p.sectors[found.sector], deployedStack: p.sectors[found.sector].deployedStack.filter((d) => d.instanceId !== found.instance.instanceId) },
    },
  }));
}

// Biodome's Planton ("Discard a ship card on your board and claim the top
// card on the level 1 deck.") - the pick resolves the discard AND the
// claim in one step; there is nothing else to decide.
export function resolveShipDiscard(state, { playerId, choiceIndex, instanceId }) {
  const choice = state.meta.pendingShipDiscards?.[choiceIndex];
  if (!choice || choice.playerId !== playerId) return state;
  const player = state.players.find((p) => p.id === playerId);
  const found = findBoardInstance(player, instanceId);
  // "a SHIP card" - a colony is not a legal discard (same permanence
  // rule as everywhere else colonies are special-cased).
  if (!found || state.colonies.some((c) => c.cardId === found.instance.cardId)) return state;
  let nextState = removeBoardInstance(state, playerId, found);
  const draw = nextState.shipyard.draw[choice.claimLevel];
  if (draw.length) {
    const [topCardId, ...rest] = draw;
    nextState = {
      ...nextState,
      shipyard: { ...nextState.shipyard, draw: { ...nextState.shipyard.draw, [choice.claimLevel]: rest } },
    };
    const claimedDef = defFor(topCardId);
    nextState = sectorHasColony(nextState.players.find((p) => p.id === playerId), claimedDef.sector)
      ? deployCardIntoColonizedSector(nextState, playerId, claimedDef, claimedDef.sector)
      : deployCard(nextState, playerId, claimedDef, claimedDef.sector);
  }
  return {
    ...nextState,
    meta: {
      ...nextState.meta,
      pendingShipDiscards: nextState.meta.pendingShipDiscards.filter((_, i) => i !== choiceIndex),
    },
  };
}

// Biodome's Thrivax - the "one die" pick queued by allocateDice. The
// chosen die's sector resolves as a normal hit for that player (blue on
// their own allocation, red as a passive player), with every pending
// queue a real hit can produce merged the standard way.
export function resolveDieRewardChoice(state, { playerId, choiceIndex, sector }) {
  const choice = state.meta.pendingDieRewardChoices?.[choiceIndex];
  if (!choice || choice.playerId !== playerId || !choice.options.includes(sector)) return state;
  const result = applyHit(state, playerId, sector, choice.isActive, false, null);
  const nextState = result.state;
  return {
    ...nextState,
    meta: {
      ...nextState.meta,
      pendingDieRewardChoices: nextState.meta.pendingDieRewardChoices.filter((_, i) => i !== choiceIndex),
      pendingClaims: [...(nextState.meta.pendingClaims || []), ...result.pendingClaims],
      pendingExtraBuys: [...(nextState.meta.pendingExtraBuys || []), ...result.pendingExtraBuys],
      pendingArrowChoices: [...(nextState.meta.pendingArrowChoices || []), ...result.pendingArrowChoices],
      pendingSectorPicks: [...(nextState.meta.pendingSectorPicks || []), ...result.pendingSectorPicks],
      pendingChargeChoices: [...(nextState.meta.pendingChargeChoices || []), ...result.pendingChargeChoices],
      pendingMoveChoices: [...(nextState.meta.pendingMoveChoices || []), ...result.pendingMoveChoices],
      pendingCardSwapChoices: [...(nextState.meta.pendingCardSwapChoices || []), ...result.pendingCardSwapChoices],
      pendingEmptySectorChoices: [...(nextState.meta.pendingEmptySectorChoices || []), ...result.pendingEmptySectorChoices],
    },
  };
}

export function declineDieRewardChoice(state, { playerId, choiceIndex }) {
  const choice = state.meta.pendingDieRewardChoices?.[choiceIndex];
  if (!choice || choice.playerId !== playerId) return state;
  return {
    ...state,
    meta: {
      ...state.meta,
      pendingDieRewardChoices: state.meta.pendingDieRewardChoices.filter((_, i) => i !== choiceIndex),
    },
  };
}

// Genesis's Satkowiak (rulebook Card 130: "Choose another player. You and
// that player each give each other 1 card.") - each side of the trade is
// one of these choices: pick one of your own non-colony cards and it
// moves to the other board, physical state (charges) and all. The moved
// card arrives the way any newly-gained card does: into its own sector,
// bumping the current station occupant to deployed - or straight onto
// the deployed stack when a colony holds that sector.
export function resolveCardTradeChoice(state, { playerId, choiceIndex, instanceId }) {
  const choice = state.meta.pendingCardTradeChoices?.[choiceIndex];
  if (!choice || choice.playerId !== playerId) return state;
  const giver = state.players.find((p) => p.id === playerId);
  const found = findBoardInstance(giver, instanceId);
  if (!found) return state;
  const def = defFor(found.instance.cardId);
  // Colonies never move between boards (the same permanence rule as
  // exchangeCard's own colony guard).
  if (state.colonies.some((c) => c.cardId === def.id)) return state;
  let nextState = removeBoardInstance(state, playerId, found);
  const receiver = nextState.players.find((p) => p.id === choice.toPlayerId);
  if (!receiver) return state;
  const movedInstance = { ...found.instance };
  if (sectorHasColony(receiver, def.sector)) {
    nextState = replacePlayer(nextState, receiver.id, (p) => ({
      ...p,
      sectors: {
        ...p.sectors,
        [def.sector]: {
          ...p.sectors[def.sector],
          deployedStack: [...p.sectors[def.sector].deployedStack, {
            ...movedInstance,
            deployedCharges: Math.min(movedInstance.stationCharges || movedInstance.deployedCharges || 0, def.deployed?.ability?.squares || 0),
          }],
        },
      },
    }));
  } else {
    nextState = replacePlayer(nextState, receiver.id, (p) => {
      const target = p.sectors[def.sector];
      const bumped = target.station;
      const bumpedDeployedAbility = bumped ? defFor(bumped.cardId).deployed?.ability : null;
      const carriedCharges = bumpedDeployedAbility
        ? Math.min(bumped.stationCharges, bumpedDeployedAbility.squares)
        : 0;
      return {
        ...p,
        sectors: {
          ...p.sectors,
          [def.sector]: {
            station: { ...movedInstance, stationCharges: Math.min(movedInstance.stationCharges || 0, def.station?.ability?.squares || 0) },
            deployedStack: bumped ? [...target.deployedStack, { ...bumped, deployedCharges: carriedCharges }] : target.deployedStack,
          },
        },
      };
    });
  }
  return {
    ...nextState,
    meta: {
      ...nextState.meta,
      pendingCardTradeChoices: nextState.meta.pendingCardTradeChoices.filter((_, i) => i !== choiceIndex),
    },
  };
}

export function resolveEmptySectorChoice(state, { playerId, choiceIndex, sector }) {
  const choice = state.meta.pendingEmptySectorChoices?.[choiceIndex];
  if (!choice || choice.playerId !== playerId) return state;
  const player = state.players.find((p) => p.id === playerId);
  if (!player.sectors[sector]?.station) return state;
  const nextState = emptySector(state, playerId, sector);
  return {
    ...nextState,
    meta: {
      ...nextState.meta,
      pendingEmptySectorChoices: state.meta.pendingEmptySectorChoices.filter((_, i) => i !== choiceIndex),
    },
  };
}

// Genesis's Dalton ("Look at the top N cards on the level L deck. You
// may claim each card or put any back.") - draws straight from the DRAW
// pile (not the shipyard's own display), so this can't reuse claimCard's
// own display-membership check at all. Shared by grantReward's own
// peekAndClaim case (the real card - a plain one-time reward) and
// useAbility's own (for a hypothetical future chargeable version) - see
// resolveDeckPeek below for how each revealed card is individually
// claimed-or-returned later.
function queueDeckPeek(state, playerId, level, count) {
  const draw = state.shipyard.draw[level].slice();
  const cardIds = draw.splice(0, count);
  return {
    ...state,
    shipyard: { ...state.shipyard, draw: { ...state.shipyard.draw, [level]: draw } },
    meta: {
      ...state.meta,
      pendingDeckPeeks: [...(state.meta.pendingDeckPeeks || []), { playerId, level, cardIds }],
    },
  };
}

// Resolves Genesis's Dalton pick (see useAbility's own peekAndClaim
// comment: 2 cards already drawn off the top of the deck when the
// ability activated, sitting in `peek.cardIds`, not returned yet).
// `decisions` must cover every one of `peek.cardIds` exactly once -
// `{cardId, claim, sectorOverride}` per card. Claimed cards deploy the
// same way a normal claim would (including the colonized-sector
// carve-out - see deployCardIntoColonizedSector's own comment);
// declined ones go to the BOTTOM of the same level's draw pile, same
// idiom the Genesis colony setup rule itself uses for a discarded draw
// ("place the newly drawn card on the bottom of the deck").
export function resolveDeckPeek(state, { playerId, peekIndex, decisions }) {
  const peek = state.meta.pendingDeckPeeks?.[peekIndex];
  if (!peek || peek.playerId !== playerId) return state;
  const cardIdSet = new Set(peek.cardIds);
  if (decisions.length !== peek.cardIds.length) return state;
  if (decisions.some((d) => !cardIdSet.has(d.cardId))) return state;
  if (new Set(decisions.map((d) => d.cardId)).size !== decisions.length) return state;

  let nextState = state;
  const returnedToBottom = [];
  decisions.forEach((d) => {
    if (!d.claim) {
      returnedToBottom.push(d.cardId);
      return;
    }
    const def = defFor(d.cardId);
    if (def.variableSector && (d.sectorOverride == null || d.sectorOverride < def.sector || d.sectorOverride > 12)) {
      returnedToBottom.push(d.cardId); // invalid/missing sector choice - can't place it, so it's declined instead
      return;
    }
    const targetSector = d.sectorOverride || def.sector;
    const claimant = nextState.players.find((p) => p.id === playerId);
    nextState = sectorHasColony(claimant, targetSector)
      ? deployCardIntoColonizedSector(nextState, playerId, def, targetSector)
      : deployCard(nextState, playerId, def, targetSector);
  });

  return {
    ...nextState,
    shipyard: {
      ...nextState.shipyard,
      draw: { ...nextState.shipyard.draw, [peek.level]: [...nextState.shipyard.draw[peek.level], ...returnedToBottom] },
    },
    meta: {
      ...nextState.meta,
      pendingDeckPeeks: nextState.meta.pendingDeckPeeks.filter((_, i) => i !== peekIndex),
    },
  };
}

// Genesis's Fitzgerald ("In turn order, all players draw a level 2 card,
// and then choose an opponent. That opponent may claim the card or
// discard it.") - draws off the top of the deck exactly like
// queueDeckPeek above, but queues a `pendingCardGiftChoices` entry
// instead of a `pendingDeckPeeks` one: the DRAWING player doesn't get to
// decide claim-or-discard themselves, they only pick WHO does (see
// resolveCardGiftTarget below for the handoff). Silently does nothing if
// the level 2 deck is already empty - nothing to draw, nothing to gift.
function queueCardGift(state, playerId, level) {
  const draw = state.shipyard.draw[level].slice();
  if (!draw.length) return state;
  const cardId = draw.shift();
  return {
    ...state,
    shipyard: { ...state.shipyard, draw: { ...state.shipyard.draw, [level]: draw } },
    meta: {
      ...state.meta,
      pendingCardGiftChoices: [...(state.meta.pendingCardGiftChoices || []), { playerId, level, cardId }],
    },
  };
}

// Resolves the drawing player's own "choose an opponent" half of
// Fitzgerald. Once picked, the actual claim-or-discard decision belongs
// entirely to `targetPlayerId` - converting straight into an ordinary
// pendingDeckPeeks entry for them reuses resolveDeckPeek/RESOLVE_DECK_PEEK
// completely unchanged (that function only ever cares "does this
// `playerId` own this peek", never who originally drew the card), so
// heuristicBot.js's existing AI resolution and any future UI picker for
// deck peeks automatically covers this too.
export function resolveCardGiftTarget(state, { playerId, choiceIndex, targetPlayerId }) {
  const choice = state.meta.pendingCardGiftChoices?.[choiceIndex];
  if (!choice || choice.playerId !== playerId) return state;
  if (!targetPlayerId || targetPlayerId === playerId || !state.players.some((p) => p.id === targetPlayerId)) return state;
  return {
    ...state,
    meta: {
      ...state.meta,
      pendingCardGiftChoices: state.meta.pendingCardGiftChoices.filter((_, i) => i !== choiceIndex),
      pendingDeckPeeks: [...(state.meta.pendingDeckPeeks || []), { playerId: targetPlayerId, level: choice.level, cardIds: [choice.cardId] }],
    },
  };
}

// Gives up a pending claim/extra-buy that can't actually be fulfilled
// right now (e.g. every card at the claimed level has already been
// bought/claimed out of the deck, or a non-free extra buy the player
// can't afford). Without this, that player's claim/extra-buy would sit
// forever - and if it's the ACTIVE player's own, endTurn's own gate (see
// its comment) would permanently block their turn from ever ending,
// hard-locking the whole game for everyone. Removes the WHOLE entry, not
// just one unit of a count>1 claim - if the level is empty now it'll
// still be empty on a retry.
export function declineClaim(state, { playerId, claimIndex }) {
  const claim = state.meta.pendingClaims?.[claimIndex];
  if (!claim || claim.playerId !== playerId) return state;
  return {
    ...state,
    meta: { ...state.meta, pendingClaims: state.meta.pendingClaims.filter((_, i) => i !== claimIndex) },
  };
}

export function declineExtraBuy(state, { playerId, extraBuyIndex }) {
  const extraBuy = state.meta.pendingExtraBuys?.[extraBuyIndex];
  if (!extraBuy || extraBuy.playerId !== playerId) return state;
  return {
    ...state,
    meta: { ...state.meta, pendingExtraBuys: state.meta.pendingExtraBuys.filter((_, i) => i !== extraBuyIndex) },
  };
}

// Same reasoning as declineClaim/declineExtraBuy just above, for a
// different way this same class of gap can occur: pickSectors' own
// excludeSectors cycle guard (grantReward's own pickSectors case) can,
// after a long enough chain of pickSectors-into-pickSectors resolutions,
// exclude enough sectors that fewer than `count` legal candidates remain
// in [min, max] at all - found via selfPlaySmoke.test.js once Auel's own
// wider max:12 range joined the pool (more candidates to chain through
// before exhausting them). No real board state can reach "impossible to
// pick" any other way (every card in the base 1-6 range always has SOME
// station), so this is purely a byproduct of the engine's own
// bookkeeping, not a real rules question - resolved the same way an
// unaffordable/sold-out claim or extra buy already is.
export function declineSectorPick(state, { playerId, pickIndex }) {
  const pick = state.meta.pendingSectorPicks?.[pickIndex];
  if (!pick || pick.playerId !== playerId) return state;
  return {
    ...state,
    meta: { ...state.meta, pendingSectorPicks: state.meta.pendingSectorPicks.filter((_, i) => i !== pickIndex) },
  };
}

// Claims can belong to a passive (non-rolling) player too - see buyCard's
// own comment on the same point. The claimant is always claim.playerId,
// not necessarily state.meta.activePlayerIndex, so playerId is required
// and validated against it rather than assumed.
export function claimCard(state, {
  playerId, level, cardId, claimIndex, sectorOverride,
}) {
  const claim = state.meta.pendingClaims?.[claimIndex];
  if (!claim || claim.playerId !== playerId || claim.level !== level) return state;
  if (!state.shipyard.display[level].includes(cardId)) return state;
  const def = defFor(cardId);
  if (def.variableSector) {
    if (sectorOverride == null || sectorOverride < def.sector || sectorOverride > 12) return state;
  } else if (sectorOverride != null) {
    return state;
  }
  const claimant = state.players.find((p) => p.id === playerId);
  const targetSector = sectorOverride || def.sector;
  let nextState = sectorHasColony(claimant, targetSector)
    ? deployCardIntoColonizedSector(state, playerId, def, targetSector)
    : deployCard(state, playerId, def, sectorOverride);
  nextState = refillDisplaySlot(nextState, level, cardId);
  const claims = nextState.meta.pendingClaims.slice();
  if (claim.count > 1) {
    claims[claimIndex] = { ...claim, count: claim.count - 1 };
  } else {
    claims.splice(claimIndex, 1);
  }
  return { ...nextState, meta: { ...nextState.meta, pendingClaims: claims } };
}

// Matt reported this directly: "my starting card... a card that
// could've gone in any slot one or higher, but I wasn't given a choice
// of where to place it." Resolves a pendingSetupPlacements entry
// (createGameState.js's own comment on drawSetupCard has the full
// story) - same shape as claimCard's own variable-sector validation,
// just against a printed minSector instead of re-deriving it from a
// card def each time (the whole point of deferring this was that the
// card's own def.sector already told us the minimum before the state
// even existed yet).
export function placeSetupCard(state, { playerId, sectorOverride }) {
  const placements = state.meta.pendingSetupPlacements || [];
  const index = placements.findIndex((p) => p.playerId === playerId);
  if (index === -1) return state;
  const placement = placements[index];
  if (sectorOverride == null || sectorOverride < placement.minSector || sectorOverride > 12) return state;
  const player = state.players.find((p) => p.id === playerId);
  if (sectorHasColony(player, sectorOverride)) return state;
  const def = defFor(placement.cardId);
  let nextState = replacePlayer(state, playerId, (p) => ({ ...p, credits: p.credits - def.cost }));
  nextState = deployCard(nextState, playerId, def, sectorOverride);
  // Matt reported this directly: "the card I got before my first turn is
  // yellow. I should have received the awards on that card when I
  // installed the card." An Immediate/End of Turn Reward card fires "the
  // instant it's bought and placed on your board" (rulebook p3 - see
  // buyCard's own comment, word for word) - a starting card placed here
  // via drawSetupCard is bought and placed exactly the same way, just
  // during setup instead of a normal turn, so it needs the identical
  // immediateReward/endOfTurnReward handling buyCard already has. This
  // never fired at all before - placeSetupCard only ever deployed the
  // card, with no equivalent of buyCard's own two reward blocks.
  if (def.immediateReward) {
    const granted = grantReward(nextState, playerId, def.immediateReward);
    nextState = {
      ...granted.state,
      meta: {
        ...granted.state.meta,
        pendingClaims: [...(granted.state.meta.pendingClaims || []), ...granted.pendingClaims],
        pendingExtraBuys: [...(granted.state.meta.pendingExtraBuys || []), ...granted.pendingExtraBuys],
        pendingSectorPicks: [...(granted.state.meta.pendingSectorPicks || []), ...granted.pendingSectorPicks],
        pendingChargeChoices: [...(granted.state.meta.pendingChargeChoices || []), ...granted.pendingChargeChoices],
        pendingMoveChoices: [...(granted.state.meta.pendingMoveChoices || []), ...granted.pendingMoveChoices],
        pendingCardSwapChoices: [...(granted.state.meta.pendingCardSwapChoices || []), ...granted.pendingCardSwapChoices],
        pendingEmptySectorChoices: [...(granted.state.meta.pendingEmptySectorChoices || []), ...granted.pendingEmptySectorChoices],
      },
    };
  }
  if (def.endOfTurnReward) {
    const entries = [{ playerId, reward: def.endOfTurnReward }];
    if (def.endOfTurnSecondaryReward) entries.push({ playerId, reward: def.endOfTurnSecondaryReward });
    nextState = {
      ...nextState,
      meta: {
        ...nextState.meta,
        pendingEndOfTurnRewards: [...(nextState.meta.pendingEndOfTurnRewards || []), ...entries],
      },
    };
  }
  return {
    ...nextState,
    meta: {
      ...nextState.meta,
      pendingSetupPlacements: placements.filter((_, i) => i !== index),
    },
  };
}

function applyIncomeStep(player) {
  if (player.credits < player.income) return { ...player, credits: player.income };
  return player;
}

export function endTurn(state) {
  if (state.meta.turnPhase !== 'post-buy') return state;
  const activePlayerId = state.players[state.meta.activePlayerIndex].id;
  // ANY player's pending decision blocks ending the turn, not just the
  // active player's own. Matt reported this directly: "when [my] deployed
  // cards are triggered by an opponent and they require a decision, the
  // game just kind of lets them end their turn and then I try to make my
  // decision but... it's already kind of my turn - it's annoying." A
  // passive player's reactive claim/charge/move/etc (e.g. l2-kovalyonok's
  // deployed reward, or a red-timed ability triggering off someone else's
  // roll) is always resolvable immediately, not just on that player's own
  // turn (claimCard/declineClaim/buyCard's extra-buy path and every other
  // resolve*/decline* function here carry no turnPhase gate of their own,
  // and the AI already auto-resolves its own via driveAITurnsStep) - so
  // this can't deadlock, it just forces the decision to be made in the
  // moment it's triggered instead of bleeding into whoever's turn is next.
  if (state.meta.pendingClaims?.length) return state;
  if (state.meta.pendingExtraBuys?.length) return state;
  if (state.meta.pendingArrowChoices?.length) return state;
  if (state.meta.pendingSectorPicks?.length) return state;
  if (state.meta.pendingDeckPeeks?.length) return state;
  if (state.meta.pendingChargeChoices?.length) return state;
  if (state.meta.pendingMoveChoices?.length) return state;
  if (state.meta.pendingCardSwapChoices?.length) return state;
  if (state.meta.pendingEmptySectorChoices?.length) return state;
  if (state.meta.pendingCardGiftChoices?.length) return state;
  if (state.meta.pendingSetupPlacements?.length) return state;
  if (state.meta.pendingBonusRolls?.length) return state;
  if (state.meta.pendingNeighborChoices?.length) return state;
  if (state.meta.pendingShipDiscards?.length) return state;
  if (state.meta.pendingDieRewardChoices?.length) return state;
  if (state.meta.pendingCardTradeChoices?.length) return state;

  let nextState = replacePlayer(state, activePlayerId, applyIncomeStep);

  // Genesis's "End of Turn" rewards (see buyCard's own comment on where
  // these get queued) - resolved right after the income step above,
  // matching the rulebook's own ordering, once, for whichever of THIS
  // finishing player's own queued rewards are still outstanding. Every
  // other player's own queued entries (a passive extra-buy's own End of
  // Turn reward, still waiting on THEIR turn to end) carry over untouched.
  // `allPlayers` (Paquette's own colony - see buyCard's own comment)
  // broadcasts to everyone instead of just the original buyer.
  (nextState.meta.pendingEndOfTurnRewards || [])
    .filter((r) => r.playerId === activePlayerId)
    .forEach((r) => {
      if (r.allPlayers) {
        nextState.players.forEach((p) => {
          nextState = grantReward(nextState, p.id, r.reward).state;
        });
      } else {
        nextState = grantReward(nextState, activePlayerId, r.reward).state;
      }
    });
  nextState = {
    ...nextState,
    meta: {
      ...nextState.meta,
      pendingEndOfTurnRewards: (nextState.meta.pendingEndOfTurnRewards || []).filter((r) => r.playerId !== activePlayerId),
    },
  };

  const finishedPlayerIndex = nextState.meta.activePlayerIndex;

  let gameEndTriggered = nextState.meta.gameEndTriggered;
  let finalTurnPlayerIndex = nextState.meta.finalTurnPlayerIndex;
  if (!gameEndTriggered) {
    // Rulebook p.16, verified word for word: "When any player reaches
    // at least 40 [VP], it triggers the end of the game, EVEN IF THIS
    // HAPPENS ON ANOTHER PLAYER'S TURN." Matt reported this directly:
    // "I'm at 40 points, but the game isn't ending" - he'd crossed the
    // threshold as a PASSIVE player (a red/deployed reward off someone
    // else's own roll, not his own turn ending) - this used to only
    // ever check finishedPlayer's own vp (whoever's turn was actually
    // ending), so a threshold crossed by anyone ELSE was silently never
    // detected at all until (if ever) turns happened to rotate back
    // around to THEIR own end-of-turn check.
    const triggerIndex = nextState.players.findIndex((p) => p.vp >= WIN_VP_THRESHOLD);
    if (triggerIndex !== -1) {
      // Biodome's Grovego ("If a player reaches 40 VP this round, do not
      // end the game. Instead play 1 more round.") - a ONE-SHOT
      // suppression of this trigger. The over-40 player is still over 40
      // at the very next turn end, so the trigger simply lands one turn
      // later - which, with the equal-turns finish below, is exactly one
      // extra round for everyone.
      if (nextState.meta.gameEndDelayArmed) {
        nextState = { ...nextState, meta: { ...nextState.meta, gameEndDelayArmed: false } };
      } else {
        gameEndTriggered = true;
        // Finish the round so everyone gets an equal number of turns - the
        // last turn belongs to the player immediately before the trigger
        // player in turn order (rulebook p16). "Before" follows the table's
        // CURRENT direction - Nikovsky (turnDirection: -1) flips it.
        const direction = nextState.meta.turnDirection || 1;
        finalTurnPlayerIndex = (triggerIndex - direction + nextState.players.length) % nextState.players.length;
      }
    }
  }

  const isGameOver = gameEndTriggered && finishedPlayerIndex === finalTurnPlayerIndex;

  // Biodome's Grippen ("After the last turn of the game, make 3
  // additional rolls. All players gain red rewards from their deployed
  // cards for each roll.") - resolved once, right before winners are
  // decided (the rewards are real VP that can change who wins). Each
  // ritual roll grants every player their deployed-zone rewards for the
  // roll's SUM sector - the same default this engine already uses for
  // any allocation with no allocator (resolveBonusRoll's own 'sum').
  // Any pending choices a deployed reward would normally queue are
  // deliberately dropped: the game is over, there is no later window to
  // resolve them in.
  if (isGameOver && nextState.meta.endgameRitualRolls && !nextState.meta.endgameRitualDone) {
    let { rngState } = nextState.meta;
    for (let i = 0; i < nextState.meta.endgameRitualRolls; i += 1) {
      const [pair, nextRng] = rollTwoDice(rngState);
      rngState = nextRng;
      const sumSector = pair[0] + pair[1];
      nextState.players.forEach((p) => {
        nextState = applyHit(nextState, p.id, sumSector, false, false, null).state;
      });
    }
    nextState = { ...nextState, meta: { ...nextState.meta, rngState, endgameRitualDone: true } };
  }

  if (isGameOver) {
    const maxVp = Math.max(...nextState.players.map((p) => p.vp));
    const winners = nextState.players.filter((p) => p.vp === maxVp);
    if (winners.length > 1) {
      // Tie: rulebook p16 - play one more full round, then re-check.
      // Modeled by simply NOT locking in a winner yet and letting play
      // continue; finalTurnPlayerIndex rolls forward by one more lap
      // (following the table's current direction - see Nikovsky).
      const direction = nextState.meta.turnDirection || 1;
      finalTurnPlayerIndex = (finalTurnPlayerIndex - direction + nextState.players.length) % nextState.players.length;
    } else {
      nextState = { ...nextState, meta: { ...nextState.meta, winnerIds: [winners[0].id] } };
    }
  }

  // Biodome's Exanik/Rinova ("Take another turn after this one.") - the
  // finishing player goes again instead of passing the turn, consuming
  // one queued grant. Nikovsky's reversed direction applies to a normal
  // advance.
  const direction = nextState.meta.turnDirection || 1;
  let nextActiveIndex = (finishedPlayerIndex + direction + nextState.players.length) % nextState.players.length;
  let extraTurnPlayerIds = nextState.meta.extraTurnPlayerIds || [];
  const finishedId = nextState.players[finishedPlayerIndex].id;
  if (extraTurnPlayerIds.includes(finishedId)) {
    nextActiveIndex = finishedPlayerIndex;
    const consumeIndex = extraTurnPlayerIds.indexOf(finishedId);
    extraTurnPlayerIds = extraTurnPlayerIds.filter((_, i) => i !== consumeIndex);
  }
  return {
    ...nextState,
    meta: {
      ...nextState.meta,
      gameEndTriggered,
      finalTurnPlayerIndex,
      activePlayerIndex: nextActiveIndex,
      turnNumber: nextState.meta.turnNumber + 1,
      turnPhase: 'pre-roll',
      dice: null,
      extraTurnPlayerIds,
      // The gate above guarantees the FINISHED player has none of their
      // own left - but a passive player's still-unresolved claim/extra-buy
      // (see this function's own comment) must survive into the next
      // turn, not be silently wiped just because someone else ended theirs.
      pendingClaims: (nextState.meta.pendingClaims || []).filter((c) => c.playerId !== activePlayerId),
      pendingExtraBuys: (nextState.meta.pendingExtraBuys || []).filter((eb) => eb.playerId !== activePlayerId),
      pendingArrowChoices: (nextState.meta.pendingArrowChoices || []).filter((c) => c.playerId !== activePlayerId),
      pendingSectorPicks: (nextState.meta.pendingSectorPicks || []).filter((p) => p.playerId !== activePlayerId),
      pendingDeckPeeks: (nextState.meta.pendingDeckPeeks || []).filter((p) => p.playerId !== activePlayerId),
      pendingChargeChoices: (nextState.meta.pendingChargeChoices || []).filter((p) => p.playerId !== activePlayerId),
      pendingMoveChoices: (nextState.meta.pendingMoveChoices || []).filter((p) => p.playerId !== activePlayerId),
      pendingCardSwapChoices: (nextState.meta.pendingCardSwapChoices || []).filter((p) => p.playerId !== activePlayerId),
      pendingEmptySectorChoices: (nextState.meta.pendingEmptySectorChoices || []).filter((p) => p.playerId !== activePlayerId),
      pendingCardGiftChoices: (nextState.meta.pendingCardGiftChoices || []).filter((p) => p.playerId !== activePlayerId),
      pendingSetupPlacements: (nextState.meta.pendingSetupPlacements || []).filter((p) => p.playerId !== activePlayerId),
      // "Keep unspent credits" (abilities.js's own keepUnspentCredits
      // case) only ever lasts "this turn" - cleared for the finishing
      // player only, same carry-over convention as everything else here.
      keepsUnspentCreditsPlayerIds: (nextState.meta.keepsUnspentCreditsPlayerIds || []).filter((id) => id !== activePlayerId),
      // Genesis's Reese/Nunn/Yaple/Veger - "until the end of YOUR NEXT
      // turn" (not just "this turn"), so this decrements once per the
      // buyer's own finished turn rather than clearing outright - see
      // buyCard's own comment for the turnsRemaining:2 lifecycle.
      resourceModifiers: decrementResourceModifiers(nextState.meta.resourceModifiers, activePlayerId),
      // Genesis's Vlad - same "until the end of your next turn" window,
      // single global slot instead of per-player.
      forcedDieValue: (nextState.meta.forcedDieValue && nextState.meta.forcedDieValue.buyerId === activePlayerId)
        ? (nextState.meta.forcedDieValue.turnsRemaining - 1 > 0
          ? { ...nextState.meta.forcedDieValue, turnsRemaining: nextState.meta.forcedDieValue.turnsRemaining - 1 }
          : null)
        : nextState.meta.forcedDieValue,
    },
  };
}

function decrementResourceModifiers(resourceModifiers, activePlayerId) {
  if (!resourceModifiers?.[activePlayerId]?.length) return resourceModifiers;
  const remaining = resourceModifiers[activePlayerId]
    .map((m) => ({ ...m, turnsRemaining: m.turnsRemaining - 1 }))
    .filter((m) => m.turnsRemaining > 0);
  return { ...resourceModifiers, [activePlayerId]: remaining };
}
